import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import SignatureCanvas from 'react-signature-canvas';
import ClockInAckCard from './ClockInAckCard';
import WorkNotesBox from './WorkNotesBox';
import { printTimesheet, timesheetHtml, serviceText, crewNotesText, fmtNoteDay } from '../lib/timesheetPrint';
import FieldItemPicker from './FieldItemPicker';
import { SheetWoEditor, AddPersonRow } from './TimesheetSheetTools';
import { send as sendOrQueue, pendingOfType, subscribe as subscribeOutbox } from '../lib/offline';
import {
  Clock, MapPin, Truck, Package, FileText, CheckCircle, XCircle, Search,
  Plus, ChevronDown, Edit2, Trash2, Send, RefreshCw, Users, PlayCircle, StopCircle,
  AlertTriangle, ChevronLeft, ChevronRight, UserX, Ban, UserCheck, DollarSign, History, X, Plane, Printer, Building2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/field`;

const ATT_LABELS = { sick: 'SICK', vacation: 'VAC', absent: 'ABSENT', on_site: 'ON SITE', no_work: 'NO WORK' };
const ATT_BADGE = {
  sick: 'bg-red-100 text-red-700 border-red-300',
  vacation: 'bg-blue-100 text-blue-700 border-blue-300',
  absent: 'bg-amber-100 text-amber-700 border-amber-300',
  on_site: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  no_work: 'bg-slate-200 text-slate-700 border-slate-400',
};
const SHOP_API = `${process.env.REACT_APP_BACKEND_URL}/api/shop`;

const localDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const today = () => localDate(new Date());
const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Toronto' }); }
  catch { return ''; }
};

// Shows the location capture outcome for a clock event: on-site, off-site,
// permission denied, couldn't get a fix, etc.
const LocationBadge = ({ c, className = '' }) => {
  const hasCoords = !!(c.clock_in_lat || c.location?.lat);
  let label, cls, Icon = MapPin;
  if (c.gps_verified === true) { label = 'On-site'; cls = 'text-emerald-600'; }
  else if (hasCoords) { label = c.gps_distance_m ? `${c.gps_distance_m}m from site` : 'Location logged'; cls = 'text-amber-600'; }
  else if (c.gps_status === 'denied') { label = 'Location denied'; cls = 'text-red-600'; Icon = Ban; }
  else if (c.gps_status === 'unavailable') { label = "Couldn't get location"; cls = 'text-amber-600'; Icon = AlertTriangle; }
  else if (c.gps_status === 'timeout') { label = 'Location timed out'; cls = 'text-amber-600'; Icon = AlertTriangle; }
  else if (c.gps_status === 'no_device') { label = 'No GPS on device'; cls = 'text-slate-500'; Icon = AlertTriangle; }
  else { label = 'No location'; cls = 'text-slate-500'; }
  return (
    <span className={`inline-flex items-center gap-1 ${cls} ${className}`} title={`Location at clock-in: ${label}`}>
      <Icon className="w-3 h-3" />{label}
    </span>
  );
};

// PSI clock-ins: did the employee acknowledge, and where were they when they did?
const AckBadge = ({ c }) => {
  if (c.is_contractor || !c.clocked_in_by) return null;
  if (!c.ack_at) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 ml-2" data-testid={`ack-badge-${c.employee_id}`} title={`Clocked in by ${c.clocked_in_by} — not acknowledged yet`}>
        <Clock className="w-3 h-3" />Not acknowledged
      </span>
    );
  }
  let label, cls;
  if (c.ack_on_site === true) { label = 'on site'; cls = 'text-emerald-600'; }
  else if (c.ack_distance_m != null) {
    label = `${c.ack_distance_m >= 1000 ? (c.ack_distance_m / 1000).toFixed(1) + ' km' : c.ack_distance_m + ' m'} from ${c.ack_ref === 'site' ? 'site' : 'foreman'}`;
    cls = 'text-amber-600';
  } else if (c.ack_lat != null) { label = 'location logged'; cls = 'text-emerald-600'; }
  else { label = c.ack_gps_status === 'denied' ? 'location denied' : 'no GPS'; cls = 'text-red-600'; }
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] ml-2 ${cls}`} data-testid={`ack-badge-${c.employee_id}`}
      title={c.ack_lat != null ? `Acknowledged at ${Number(c.ack_lat).toFixed(5)}, ${Number(c.ack_lng).toFixed(5)}` : 'Acknowledged without a GPS fix'}>
      <CheckCircle className="w-3 h-3" />Acknowledged {fmtTime(c.ack_at)} · {label}
    </span>
  );
};

const urlParams = () => new URLSearchParams(window.location.search);

// Approved day: shows the OT split in force and lets the PM flip it without un-approving.
// 'dt:stat_holiday' / 'dt:breakdown' = every worked hour charged and paid at double time.
const OT_MODES = [
  { key: '8_ot', label: '8 + OT', on: 'bg-emerald-600 text-white', off: 'text-emerald-700 hover:bg-emerald-50', title: 'First 8h regular, rest overtime' },
  { key: 'all_reg', label: 'All Reg', on: 'bg-blue-600 text-white', off: 'text-blue-700 hover:bg-blue-50', title: 'Every hour regular (no overtime)' },
  { key: 'all_ot', label: 'All OT', on: 'bg-amber-600 text-white', off: 'text-amber-700 hover:bg-amber-50', title: 'Every hour overtime' },
  { key: 'dt:stat_holiday', label: 'Stat 2×', on: 'bg-rose-600 text-white', off: 'text-rose-700 hover:bg-rose-50', title: 'Stat holiday — every hour charged and paid at double time' },
  { key: 'dt:breakdown', label: 'Brkdn 2×', on: 'bg-rose-600 text-white', off: 'text-rose-700 hover:bg-rose-50', title: 'Breakdown call — every hour charged and paid at double time' },
];
const DT_LABEL = { stat_holiday: 'Stat holiday', breakdown: 'Breakdown' };
const otKey = (mode, reason) => (mode === 'all_dt' ? `dt:${reason || 'stat_holiday'}` : mode);
const otBody = (key) => (key?.startsWith('dt:') ? { ot_mode: 'all_dt', dt_reason: key.slice(3) } : { ot_mode: key });
const otLabel = (key) => (key?.startsWith('dt:') ? `double time (${DT_LABEL[key.slice(3)] || key.slice(3)})` : key === 'all_ot' ? 'all OT' : key === 'all_reg' ? 'all regular' : '8h + OT');
const OtModeToggle = ({ current, onPick, testPrefix, busy }) => (
  <span className="inline-flex items-center rounded-md border border-slate-300 bg-white overflow-hidden" data-testid={`${testPrefix}-ot-toggle`}
    title="Change how this approved day splits regular / overtime / double time — stays approved">
    {OT_MODES.map(m => {
      const active = (current || '8_ot') === m.key;
      return (
        <button key={m.key} type="button" disabled={busy}
          onClick={(e) => { e.stopPropagation(); if (!active) onPick(m.key); }}
          data-testid={`${testPrefix}-ot-${m.key.replace(':', '-')}`} aria-pressed={active} title={m.title}
          className={`px-1.5 py-0.5 text-[10px] font-semibold transition-colors disabled:opacity-60 ${active ? m.on : m.off}`}>
          {m.label}
        </button>
      );
    })}
  </span>
);

export default function FieldTracker() {
  const [tab, setTab] = useState(() => {
    const t = urlParams().get('tab');
    return ['clock', 'timesheet', 'approve', 'crew', 'rates', 'admin'].includes(t) ? t : 'timesheet';
  });
  const { user } = useAuth();

  const isForeman = ['super_admin', 'admin', 'shop_foreman', 'millwright_foreman', 'project_manager', 'project_manager_estimator'].includes(user?.role);
  const isPMRole = ['super_admin', 'admin', 'project_manager', 'project_manager_estimator'].includes(user?.role);
  const isPM = isPMRole || (user?.extra_permissions || []).includes('field_review');

  const isSuperAdmin = user?.role === 'super_admin';

  const tabs = [
    { id: 'clock', label: 'Clock In/Out', icon: Clock },
    { id: 'timesheet', label: 'Timesheets', icon: FileText },
    ...(isPM ? [{ id: 'approve', label: 'PM Review', icon: CheckCircle }] : []),
    ...(isPM ? [{ id: 'crew', label: 'Field Crew', icon: Users }] : []),
    ...(isPMRole ? [{ id: 'rates', label: 'Pay Rates', icon: DollarSign }] : []),
    ...(isSuperAdmin ? [{ id: 'admin', label: 'Admin', icon: Search }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="field-tracker">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Field Tracker</h1>
        <p className="text-sm text-slate-500 mt-0.5">Digital timesheets, GPS clock-in, approvals</p>
      </div>

      <div className="flex gap-1 bg-white p-1 rounded-xl border border-slate-200 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`field-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
              tab === t.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
            }`}>
            <t.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'clock' && <ClockTab />}
      {tab === 'timesheet' && <TimesheetTab />}
      {tab === 'approve' && isPM && <ApprovalTab />}
      {tab === 'crew' && isPM && <CrewTab />}
      {tab === 'rates' && isPMRole && <RatesTab />}
      {tab === 'admin' && isSuperAdmin && <FieldAdminTab />}
    </div>
  );
}

/* ── Field timesheet billing extras (travel / truck / afternoon premium) ── */
function BillingRatesCard() {
  const [rates, setRates] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const FIELDS = [
    { key: 'travel_rate', label: 'Travel', unit: '$/hr', hint: 'Flat rate for travel hours (per way × days on site)' },
    { key: 'truck_rate', label: 'Service Truck', unit: '$/day', hint: 'Per truck, per day on site' },
    { key: 'afternoon_premium', label: 'Afternoon Shift Premium', unit: '$/hr', hint: 'Added to every afternoon-shift hour' },
  ];
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/billing-rates`);
      setRates(r.data);
      setForm({ travel_rate: r.data.travel_rate, truck_rate: r.data.truck_rate, afternoon_premium: r.data.afternoon_premium });
    } catch { toast.error('Failed to load billing rates'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = rates && FIELDS.some(f => Number(form[f.key]) !== Number(rates[f.key]));
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/billing-rates`, form);
      setRates(r.data);
      toast.success(`Billing rates saved — travel is now $${Number(r.data.travel_rate).toFixed(2)}/hr on new timesheets`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="billing-rates-card">
      <h2 className="text-slate-900 font-semibold flex items-center gap-2"><MapPin className="w-4 h-4 text-blue-700" /> Field Timesheet Billing</h2>
      <p className="text-xs text-slate-500 mt-0.5">Used on every printed T&amp;M / costing timesheet. Already-saved timesheets keep the rate they were printed with.</p>
      {!rates ? <p className="text-slate-500 text-sm mt-3">Loading…</p> : (
        <div className="mt-3 grid sm:grid-cols-3 gap-3">
          {FIELDS.map(f => (
            <label key={f.key} className="block bg-slate-50 border border-slate-200 rounded-lg p-3">
              <span className="text-xs font-semibold text-slate-700">{f.label} <span className="text-slate-500">({f.unit})</span></span>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-slate-500">$</span>
                <input type="number" step="0.25" min="0" value={form[f.key] ?? ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                  data-testid={`billing-rate-${f.key}`}
                  className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-slate-900 font-bold outline-none focus:border-blue-500" />
              </div>
              <span className="block text-[11px] text-slate-500 mt-1">{f.hint}</span>
            </label>
          ))}
        </div>
      )}
      {rates && (
        <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
          <span className="text-[11px] text-slate-500">{rates.updated_by ? `Last changed by ${rates.updated_by}${rates.updated_at ? ` · ${new Date(rates.updated_at).toLocaleDateString()}` : ''}` : 'Using default rates'}</span>
          <button onClick={save} disabled={!dirty || saving} data-testid="billing-rates-save"
            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm font-semibold">
            {saving ? 'Saving…' : 'Save rates'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Pay Rates Tab (PM / Estimators only) ─────────────────────────── */
function RatesTab() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const fmt = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/labor-rates`);
        if (active) setRates(Array.isArray(r.data) ? r.data : (r.data?.labor_rates || []));
      } catch (e) {
        toast.error('Failed to load pay rates');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  return (
    <div className="space-y-3" data-testid="field-rates-tab">
      <BillingRatesCard />
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <h2 className="text-slate-900 font-semibold flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-blue-700" /> Labour Rates by Role
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">Billed hourly rates used on field timesheets. Visible to PM / Estimators only.</p>
      </div>
      {loading ? (
        <p className="text-slate-500 text-sm px-1">Loading rates…</p>
      ) : rates.length === 0 ? (
        <p className="text-slate-500 text-sm px-1">No labour rates configured yet.</p>
      ) : (
        <div className="space-y-2">
          {rates.map(rate => (
            <div key={rate.id} data-testid={`rate-row-${rate.id}`}
              className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-4 py-3">
              <div>
                <p className="text-slate-900 font-medium">{rate.role_name}</p>
                {rate.description && <p className="text-slate-500 text-xs">{rate.description}</p>}
              </div>
              <span className="text-slate-900 font-bold whitespace-nowrap">{fmt(rate.hourly_rate)}/hr</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Clock In/Out Tab ──────────────────────────────────────────── */
function ClockTab() {
  const [clockStatus, setClockStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [woSearch, setWoSearch] = useState('');
  const [woResults, setWoResults] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [myHours, setMyHours] = useState([]);
  const [gpsStatus, setGpsStatus] = useState(null);
  const [showNewClockIn, setShowNewClockIn] = useState(false);
  const [isServiceCall, setIsServiceCall] = useState(false);
  const [isNightShift, setIsNightShift] = useState(false);
  const [isAfternoonShift, setIsAfternoonShift] = useState(false);
  const [noLunch, setNoLunch] = useState(false);
  // Self time correction
  const [editEvt, setEditEvt] = useState(null);
  const [evIn, setEvIn] = useState('');
  const [evOut, setEvOut] = useState('');
  const [evNoLunch, setEvNoLunch] = useState(false);
  const [savingEvt, setSavingEvt] = useState(false);
  const [fixOpen, setFixOpen] = useState(false);
  const [showMissed, setShowMissed] = useState(false);
  const [missedDate, setMissedDate] = useState(today());
  const [missedIn, setMissedIn] = useState('');
  const [missedOut, setMissedOut] = useState('');
  const [missedNoLunch, setMissedNoLunch] = useState(false);
  const [mWoSearch, setMWoSearch] = useState('');
  const [mWoResults, setMWoResults] = useState([]);
  const [mWO, setMWO] = useState(null);
  const [pendingClock, setPendingClock] = useState([]); // my clock-ins/outs saved on this phone, not yet sent

  // Recent field WOs from localStorage
  const getRecentFieldWOs = () => {
    try { return JSON.parse(localStorage.getItem('field_recent_wos') || '[]'); } catch { return []; }
  };
  const saveRecentFieldWO = (wo) => {
    const recents = getRecentFieldWOs().filter(r => r.project_number !== wo.project_number);
    recents.unshift({ project_number: wo.project_number, client_name: wo.client_name || '' });
    localStorage.setItem('field_recent_wos', JSON.stringify(recents.slice(0, 10)));
  };

  const fetchStatus = useCallback(async () => {
    setPendingClock(await pendingOfType(['self_clock_in', 'self_clock_out']).catch(() => []));
    try {
      const r = await axios.get(`${API}/clock-status`);
      setClockStatus(r.data);
      // Load all today's events
      const todayStr = today();
      const hrs = await axios.get(`${API}/my-hours`, { params: { date_from: todayStr, date_to: todayStr } });
      setMyHours(hrs.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStatus();
    return subscribeOutbox(() => { pendingOfType(['self_clock_in', 'self_clock_out']).then(setPendingClock).catch(() => {}); });
  }, [fetchStatus]);
  useEffect(() => {
    const onSynced = (e) => { if (['self_clock_in', 'self_clock_out'].includes(e.detail?.job?.type)) fetchStatus(); };
    window.addEventListener('offline-synced', onSynced);
    return () => window.removeEventListener('offline-synced', onSynced);
  }, [fetchStatus]);

  const getGPS = () => new Promise((resolve) => {
    if (!navigator.geolocation) { setGpsStatus('GPS not available on this device'); resolve({ gps_status: 'no_device' }); return; }
    setGpsStatus('Getting location...');
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGpsStatus('Location captured'); resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, gps_status: 'captured' }); },
      (err) => {
        const status = err.code === 1 ? 'denied' : err.code === 2 ? 'unavailable' : 'timeout';
        const msg = status === 'denied' ? 'Location permission denied' : status === 'unavailable' ? "Couldn't get location" : 'Location request timed out';
        setGpsStatus(`${msg} — clocking in without GPS`);
        resolve({ gps_status: status });
      },
      { timeout: 8000, enableHighAccuracy: true, maximumAge: 30000 }
    );
  });

  const searchWO = async (q) => {
    setWoSearch(q);
    setSelectedWO(null);
    if (q.length < 1) {
      // Show recents when empty
      const recents = getRecentFieldWOs();
      setWoResults(recents);
      setShowDropdown(recents.length > 0);
      return;
    }
    try {
      const r = await axios.get(`${API}/work-orders`, { params: { q } });
      setWoResults(r.data.slice(0, 8));
      setShowDropdown(true);
    } catch {
      // No signal: the last full list this phone saw (kept by the offline cache) + recents, filtered here
      try {
        const cached = await axios.get(`${API}/work-orders`, { params: { q: '' } });
        const ql = q.toLowerCase();
        const pool = [...getRecentFieldWOs(), ...(cached.data || [])];
        const seen = new Set();
        const hits = pool.filter(w => { const k = w.project_number; if (seen.has(k)) return false; seen.add(k); return `${k} ${w.client_name || ''} ${w.title || ''}`.toLowerCase().includes(ql); });
        setWoResults(hits.slice(0, 8));
        setShowDropdown(hits.length > 0);
      } catch {
        const hits = getRecentFieldWOs().filter(w => `${w.project_number} ${w.client_name || ''}`.toLowerCase().includes(q.toLowerCase()));
        setWoResults(hits);
        setShowDropdown(hits.length > 0);
      }
    }
  };

  const handleWOFocus = () => {
    if (!selectedWO) {
      const recents = getRecentFieldWOs();
      if (recents.length > 0 && !woSearch) {
        setWoResults(recents);
        setShowDropdown(true);
      } else if (woSearch.length >= 1) {
        searchWO(woSearch);
      }
    }
  };

  const selectWO = (wo) => {
    setSelectedWO(wo);
    setWoSearch(wo.project_number);
    setWoResults([]);
    setShowDropdown(false);
  };

  const clockIn = async () => {
    if (!selectedWO) { toast.error('Select a work order from the list'); return; }
    setSubmitting(true);
    const gps = await getGPS();
    try {
      const at = new Date().toISOString();
      const sent = await sendOrQueue('self_clock_in', `Clock in on ${selectedWO.project_number} at ${fmtTime(at)}`, {
        work_order_number: selectedWO.project_number,
        is_service_call: isServiceCall,
        night_shift: isNightShift,
        afternoon_shift: isAfternoonShift,
        at,
        ...(gps || {}),
      });
      saveRecentFieldWO(selectedWO);
      if (sent.queued) toast.warning('No signal — clock-in saved on this phone. It posts on its own when you are back in service.', { duration: 6000 });
      else toast.success('Clocked in!');
      setShowNewClockIn(false);
      fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  const [workNotes, setWorkNotes] = useState('');
  const clockOut = async () => {
    setSubmitting(true);
    const gps = await getGPS();
    try {
      const at = new Date().toISOString();
      const sent = await sendOrQueue('self_clock_out', `Clock out at ${fmtTime(at)}`, { took_lunch: !noLunch, work_notes: workNotes, at, ...(gps || {}) });
      if (sent.queued) toast.warning('No signal — clock-out saved on this phone. It posts on its own when you are back in service.', { duration: 6000 });
      else toast.success('Clocked out!');
      fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  const toETTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Toronto' }) : '';

  const openEvtEdit = (h) => {
    setEditEvt(h.id);
    setEvIn(toETTime(h.clock_in_time));
    setEvOut(toETTime(h.clock_out_time));
    setEvNoLunch(h.took_lunch === false);
  };

  const saveEvtEdit = async (h) => {
    if (!evIn || !evOut) { toast.error('Enter both times'); return; }
    setSavingEvt(true);
    try {
      await axios.put(`${SHOP_API}/field-crew-edit/${h.id}`, {
        clock_in_time: `${h.date}T${evIn}`,
        clock_out_time: `${h.date}T${evOut}`,
        took_lunch: !evNoLunch,
      });
      toast.success('Time corrected — flagged for office review');
      setEditEvt(null);
      fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingEvt(false);
  };

  const saveActiveFix = async () => {
    if (!evIn || !event?.id) return;
    setSavingEvt(true);
    try {
      await axios.put(`${SHOP_API}/field-crew-edit/${event.id}`, { clock_in_time: `${event.date}T${evIn}` });
      toast.success('Clock-in time fixed');
      setFixOpen(false);
      fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingEvt(false);
  };

  const searchMissedWO = async (q) => {
    setMWoSearch(q);
    setMWO(null);
    if (q.length < 1) { setMWoResults(getRecentFieldWOs()); return; }
    try {
      const r = await axios.get(`${API}/work-orders`, { params: { q } });
      setMWoResults(r.data.slice(0, 6));
    } catch { setMWoResults([]); }
  };

  const submitMissed = async () => {
    if (!mWO || !missedIn || !missedOut) { toast.error('Pick the job and enter both times'); return; }
    setSavingEvt(true);
    try {
      await axios.post(`${SHOP_API}/field-crew-add`, {
        date: missedDate,
        work_order_number: mWO.project_number,
        clock_in_time: missedIn,
        clock_out_time: missedOut,
        took_lunch: !missedNoLunch,
      });
      toast.success('Missed time added — flagged for office review');
      setShowMissed(false); setMWO(null); setMWoSearch(''); setMissedIn(''); setMissedOut('');
      fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingEvt(false);
  };

  if (loading) return <div className="text-center py-8 text-slate-500">Loading...</div>;

  // A clock-in/out saved on this phone (no signal) overrides what the server last knew
  const lastPending = pendingClock[pendingClock.length - 1];
  const pendingIn = lastPending?.type === 'self_clock_in' ? lastPending : null;
  const pendingOut = lastPending?.type === 'self_clock_out' ? lastPending : null;
  const event = pendingIn
    ? { work_order_number: pendingIn.payload.work_order_number, clock_in_time: pendingIn.payload.at, is_service_call: pendingIn.payload.is_service_call,
        night_shift: pendingIn.payload.night_shift, afternoon_shift: pendingIn.payload.afternoon_shift, offline_pending: true }
    : clockStatus?.event;
  const isClockedIn = pendingIn ? true : pendingOut ? false : (clockStatus?.clocked_in && !clockStatus?.clocked_out);
  const isClockedOut = pendingOut ? true : (clockStatus?.clocked_in && clockStatus?.clocked_out);
  const completedEvents = myHours.filter(h => h.clock_out_time);
  const showClockInForm = !isClockedIn && (showNewClockIn || !isClockedOut);

  return (
    <div className="space-y-4 max-w-2xl">
      <ClockInAckCard onAcked={fetchStatus} />
      {/* Active — clocked in */}
      {isClockedIn && (
        <>
        <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5" data-testid="field-clocked-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-700 animate-pulse" />
              </div>
              <div>
                <p className="text-slate-900 font-medium">On site — {event?.work_order_number} <a href={`/tool-crib?wo=${event?.work_order_number}`} className="ml-2 text-xs text-emerald-700 underline" data-testid="field-kit-link">🧰 what's in the truck</a></p>
                <p className="text-xs text-emerald-700">Clocked in at {fmtTime(event?.clock_in_time)}{event?.clocked_in_by ? ` by ${event.clocked_in_by}` : ''}</p>
                {event?.offline_pending && <p className="text-xs text-amber-700 flex items-center gap-1" data-testid="field-clockin-offline-pending"><AlertTriangle className="w-3 h-3" /> Saved on this phone — sends when back in service</p>}
                {event?.ack_at && <p className="text-xs text-emerald-700 flex items-center gap-1" data-testid="field-clocked-in-acked"><CheckCircle className="w-3 h-3" /> Acknowledged {fmtTime(event.ack_at)}</p>}
                {event?.gps_verified && <p className="text-xs text-emerald-700 flex items-center gap-1"><MapPin className="w-3 h-3" /> GPS verified</p>}
                {event?.gps_verified === false && event?.gps_distance_m && (
                  <p className="text-xs text-amber-700 flex items-center gap-1"><MapPin className="w-3 h-3" /> {event.gps_distance_m}m from site</p>
                )}
                {!event?.gps_verified && !event?.clock_in_lat && event?.gps_status === 'denied' && (
                  <p className="text-xs text-red-600 flex items-center gap-1"><Ban className="w-3 h-3" /> Location was denied at clock-in</p>
                )}
                {!event?.gps_verified && !event?.clock_in_lat && (event?.gps_status === 'unavailable' || event?.gps_status === 'timeout' || event?.gps_status === 'no_device') && (
                  <p className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Couldn't get location at clock-in</p>
                )}
              </div>
            </div>
            <button onClick={clockOut} disabled={submitting} data-testid="field-clock-out-btn"
              className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-200 text-white rounded-xl font-semibold text-sm flex items-center gap-2">
              <StopCircle className="w-4 h-4" /> Clock Out
            </button>
          </div>
          <div className="flex items-center gap-4 mt-2">
            {event?.is_service_call && <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded border border-amber-200">Service Call (4hr min)</span>}
            {event?.night_shift && <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-200" data-testid="night-shift-badge">🌙 Night shift</span>}
            {event?.afternoon_shift && <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded border border-orange-200" data-testid="afternoon-shift-badge">🌇 Afternoon shift · $2/hr premium</span>}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={noLunch} onChange={e => setNoLunch(e.target.checked)} className="rounded border-slate-500" />
              <span className="text-xs text-slate-700">No lunch taken</span>
            </label>
          </div>
          {gpsStatus && <p className="text-xs text-amber-700 mt-2">{gpsStatus}</p>}
          {!event?.offline_pending && <WorkNotesBox event={event} onChange={setWorkNotes} onSaved={fetchStatus} />}
          {event?.offline_pending && (
            <textarea value={workNotes} onChange={e => setWorkNotes(e.target.value)} rows={2} placeholder="What did you do today? (goes with your clock-out)"
              className="mt-3 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="field-offline-work-notes" />
          )}
          <div className="mt-2">
            {event?.offline_pending ? null : fixOpen ? (
              <div className="flex items-center gap-2">
                <input type="time" value={evIn} onChange={e => setEvIn(e.target.value)}
                  className="px-2 py-1.5 rounded bg-white text-slate-900 text-sm" data-testid="fix-clockin-time-input" />
                <button onClick={saveActiveFix} disabled={savingEvt}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-semibold" data-testid="fix-clockin-save">Save</button>
                <button onClick={() => setFixOpen(false)} className="text-xs text-slate-500">Cancel</button>
              </div>
            ) : (
              <button onClick={() => { setFixOpen(true); setEvIn(toETTime(event?.clock_in_time)); }}
                className="text-xs text-blue-700 underline" data-testid="fix-clockin-btn">
                Wrong start time? Fix it
              </button>
            )}
          </div>
        </div>
        <FieldConsumablePicker workOrder={event?.work_order_number} />
        </>
      )}

      {/* Completed jobs today */}
      {!isClockedIn && completedEvents.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-slate-700">Today's Jobs</h3>
            {!showNewClockIn && (
              <button onClick={() => setShowNewClockIn(true)}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2">
                <PlayCircle className="w-4 h-4" /> Clock In to New Job
              </button>
            )}
          </div>
          <div className="space-y-2">
            {completedEvents.map(h => (
              <div key={h.id} className="px-3 py-2 rounded-lg bg-slate-900/10 border border-slate-300">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-mono text-blue-700 text-sm font-bold">{h.work_order_number}</span>
                    <span className="text-xs text-slate-500 ml-2">{fmtTime(h.clock_in_time)} — {fmtTime(h.clock_out_time)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {h.gps_verified && <MapPin className="w-3 h-3 text-emerald-700" />}
                    {h.gps_verified === false && <MapPin className="w-3 h-3 text-amber-700" />}
                    <span className="text-slate-900 font-bold text-sm">{h.hours_worked}h</span>
                    <button onClick={() => editEvt === h.id ? setEditEvt(null) : openEvtEdit(h)}
                      className="text-slate-500 hover:text-blue-700 p-1" data-testid={`edit-my-event-${h.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {editEvt === h.id && (
                  <div className="mt-2 pt-2 border-t border-slate-300 flex flex-wrap items-center gap-2" data-testid="my-event-edit-form">
                    <label className="text-xs text-slate-500">In</label>
                    <input type="time" value={evIn} onChange={e => setEvIn(e.target.value)}
                      className="px-2 py-1 rounded bg-white text-slate-900 text-sm" data-testid="my-event-in-input" />
                    <label className="text-xs text-slate-500">Out</label>
                    <input type="time" value={evOut} onChange={e => setEvOut(e.target.value)}
                      className="px-2 py-1 rounded bg-white text-slate-900 text-sm" data-testid="my-event-out-input" />
                    <label className="flex items-center gap-1 text-xs text-slate-500">
                      <input type="checkbox" checked={evNoLunch} onChange={e => setEvNoLunch(e.target.checked)} /> No lunch
                    </label>
                    <button onClick={() => saveEvtEdit(h)} disabled={savingEvt}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-semibold" data-testid="my-event-save">Save</button>
                  </div>
                )}
                <WorkNotesBox event={h} compact onSaved={fetchStatus} />
              </div>
            ))}
          </div>
        </div>
      )}
      {!isClockedIn && completedEvents.length > 0 && (
        <FieldConsumablePicker workOrder={completedEvents[completedEvents.length - 1]?.work_order_number} />
      )}

      {/* Clock in form */}
      {showClockInForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="field-clock-in">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
              <PlayCircle className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <p className="text-slate-900 font-medium">Clock In to Job Site</p>
              <p className="text-xs text-slate-500">Select your work order and clock in</p>
            </div>
          </div>

          {/* WO Search */}
          <div className="relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
            <input type="text" value={woSearch}
              onChange={e => searchWO(e.target.value)}
              onFocus={handleWOFocus}
              placeholder="Search WO#, customer..." data-testid="field-wo-search"
              className="w-full pl-10 pr-4 py-3 bg-white/90 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-lg focus:border-blue-500 outline-none" />
          </div>
          {showDropdown && woResults.length > 0 && !selectedWO && (
            <div className="max-h-60 overflow-y-auto bg-white border border-slate-300 rounded-lg divide-y divide-slate-200 shadow-lg">
              {!woSearch && <div className="px-4 py-1.5 bg-white text-xs text-slate-500 font-medium uppercase tracking-wider">Recent Jobs</div>}
              {woResults.map(wo => (
                <button key={wo.project_number} data-testid={`field-wo-option-${wo.project_number}`}
                  onMouseDown={(e) => { e.preventDefault(); selectWO(wo); }}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors">
                  <span className="font-mono text-blue-700 font-bold">{wo.project_number}</span>
                  <span className="mx-2 text-slate-500">|</span>
                  <span className="text-slate-700">{wo.client_name || 'No customer'}</span>
                  {(wo.title || wo.description) && <p className="text-xs text-slate-500 mt-0.5 truncate">{wo.title || wo.description}</p>}
                </button>
              ))}
            </div>
          )}
          {selectedWO && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <div>
                <span className="font-mono text-blue-700 font-bold">{selectedWO.project_number}</span>
                <span className="mx-2 text-slate-500">|</span>
                <span className="text-slate-900">{selectedWO.client_name}</span>
              </div>
              <button onClick={() => { setSelectedWO(null); setWoSearch(''); setShowDropdown(false); }} className="text-slate-500 hover:text-slate-900">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          )}

          {selectedWO && (
            <>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isServiceCall} onChange={e => setIsServiceCall(e.target.checked)} className="rounded border-slate-500" />
                <span className="text-sm text-slate-700">Service Call (4hr minimum)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="field-night-shift-label">
                <input type="checkbox" checked={isNightShift} onChange={e => { setIsNightShift(e.target.checked); if (e.target.checked) setIsAfternoonShift(false); }} className="rounded border-slate-500" data-testid="field-night-shift-checkbox" />
                <span className="text-sm text-slate-700">🌙 Night shift (works past midnight)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="field-afternoon-shift-label">
                <input type="checkbox" checked={isAfternoonShift} onChange={e => { setIsAfternoonShift(e.target.checked); if (e.target.checked) setIsNightShift(false); }} className="rounded border-slate-500" data-testid="field-afternoon-shift-checkbox" />
                <span className="text-sm text-slate-700">🌇 Afternoon shift ($2/hr premium)</span>
              </label>
            </>
          )}

          <button onClick={clockIn} disabled={submitting || !selectedWO} data-testid="field-clock-in-btn"
            className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-2">
            <MapPin className="w-5 h-5" /> {submitting ? 'Getting GPS...' : 'Clock In'}
          </button>
          {gpsStatus && <p className="text-xs text-slate-500 text-center">{gpsStatus}</p>}
        </div>
      )}

      {/* Forgot to clock in / out? */}
      {!isClockedIn && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          {!showMissed ? (
            <button onClick={() => { setShowMissed(true); setMissedDate(today()); }}
              className="text-sm text-blue-700 font-medium" data-testid="add-missed-time-btn">
              Forgot to clock in or out? Add missed time →
            </button>
          ) : (
            <div className="space-y-3" data-testid="missed-time-form">
              <div className="flex items-center justify-between">
                <p className="text-slate-900 font-medium text-sm">Add Missed Time</p>
                <button onClick={() => setShowMissed(false)}><X className="w-4 h-4 text-slate-500" /></button>
              </div>
              <p className="text-xs text-slate-500">For a shift you forgot to clock — last 7 days only. It's flagged for office review.</p>
              <input type="date" value={missedDate} max={today()} onChange={e => setMissedDate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white text-slate-900 text-sm border border-slate-300" data-testid="missed-date-input" />
              <div className="relative">
                <input type="text" value={mWoSearch} onChange={e => searchMissedWO(e.target.value)}
                  onFocus={() => { if (!mWO && !mWoSearch) setMWoResults(getRecentFieldWOs()); }}
                  placeholder="Search WO#, customer..."
                  className="w-full px-3 py-2 rounded-lg bg-white text-slate-900 text-sm border border-slate-300" data-testid="missed-wo-input" />
                {mWoResults.length > 0 && !mWO && (
                  <div className="absolute z-10 left-0 right-0 mt-1 max-h-44 overflow-y-auto bg-white border border-slate-300 rounded-lg divide-y divide-slate-200 shadow-lg">
                    {mWoResults.map(wo => (
                      <button key={wo.project_number}
                        onMouseDown={(e) => { e.preventDefault(); setMWO(wo); setMWoSearch(wo.project_number); setMWoResults([]); }}
                        className="w-full text-left px-3 py-2 hover:bg-slate-100 text-sm">
                        <span className="font-mono text-blue-600 font-bold">{wo.project_number}</span>
                        <span className="mx-2 text-slate-500">|</span>
                        <span className="text-slate-700">{wo.client_name || 'No customer'}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-slate-500 mb-1">Start</label>
                  <input type="time" value={missedIn} onChange={e => setMissedIn(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-white text-slate-900 text-sm border border-slate-300" data-testid="missed-in-input" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-slate-500 mb-1">End</label>
                  <input type="time" value={missedOut} onChange={e => setMissedOut(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-white text-slate-900 text-sm border border-slate-300" data-testid="missed-out-input" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={missedNoLunch} onChange={e => setMissedNoLunch(e.target.checked)} className="rounded border-slate-500" />
                <span className="text-xs text-slate-700">No lunch taken</span>
              </label>
              <button onClick={submitMissed} disabled={savingEvt}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white rounded-lg font-semibold text-sm"
                data-testid="missed-submit-btn">
                {savingEvt ? 'Adding…' : 'Add Missed Time'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Timesheet Tab ──────────────────────────────────────────────── */
// printTimesheet + serviceText/crewNotesText/fmtNoteDay live in ../lib/timesheetPrint.js (shared with the Customer Portal)



function ServiceDescriptionBox({ wo, dateFrom, dateTo, onSaved }) {
  const [text, setText] = useState(wo.service_description || '');
  const [open, setOpen] = useState(!!(wo.service_description || (wo.crew_notes || []).length));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setText(wo.service_description || ''); }, [wo.work_order_number, wo.service_description]);
  const dirty = text !== (wo.service_description || '');
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/timesheet/description`, { work_order_number: wo.work_order_number, date_from: dateFrom, date_to: dateTo, description: text });
      toast.success('Service description saved — it prints on this timesheet');
      onSaved && onSaved(text);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    setSaving(false);
  };
  const insertCrew = () => {
    const add = crewNotesText(wo);
    if (!add) return;
    setText(t => (t.trim() ? `${t.trimEnd()}\n${add}` : add));
  };
  const crewCount = (wo.crew_notes || []).length;
  return (
    <div className={`px-4 py-2 border-b ${!wo.billing_type ? 'bg-amber-50/60 border-amber-200' : 'bg-slate-50/60 border-slate-200'}`} data-testid={`ts-service-${wo.work_order_number}`}>
      <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 text-xs font-semibold text-slate-700 hover:text-slate-900" data-testid={`ts-service-toggle-${wo.work_order_number}`}>
        <FileText className="w-3.5 h-3.5" /> Service covered by this timesheet
        {!open && (text.trim() ? <span className="text-slate-500 font-normal truncate max-w-[60ch]">— {text.split('\n')[0]}</span>
          : crewCount ? <span className="text-emerald-700 font-normal">— {crewCount} crew note{crewCount !== 1 ? 's' : ''} ready to insert</span>
          : <span className="text-slate-500 font-normal">— blank (prints empty lines)</span>)}
        <span className="text-slate-500">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="mt-2 grid gap-2 md:grid-cols-[1fr_auto]">
          <div>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
              placeholder="What was done this week on this job — prints under 'Service covered by this Timesheet'. Leave blank to print the crew's daily notes."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-300 bg-white"
              data-testid={`ts-service-input-${wo.work_order_number}`} />
            <div className="flex items-center gap-2 mt-1">
              <button onClick={save} disabled={saving || !dirty}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:text-slate-500 text-white rounded text-xs font-semibold"
                data-testid={`ts-service-save-${wo.work_order_number}`}>{saving ? 'Saving…' : 'Save'}</button>
              {crewCount > 0 && (
                <button onClick={insertCrew} className="px-3 py-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded text-xs font-semibold"
                  data-testid={`ts-service-insert-crew-${wo.work_order_number}`}>Insert crew notes ({crewCount})</button>
              )}
              {!text.trim() && crewCount > 0 && <span className="text-[11px] text-slate-500">Blank → the crew notes below print as-is</span>}
            </div>
          </div>
          {crewCount > 0 && (
            <div className="md:max-w-xs text-xs bg-white border border-slate-200 rounded-lg p-2 max-h-28 overflow-y-auto" data-testid={`ts-crew-notes-${wo.work_order_number}`}>
              <p className="font-semibold text-slate-700 mb-1">Crew notes this week</p>
              {(wo.crew_notes || []).map((n, i) => (
                <p key={i} className="text-slate-700"><span className="text-slate-500">{fmtNoteDay(n.date)}</span> · <span className="font-medium">{n.employee_name}</span>: {n.notes}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TimesheetTab() {
  const { user } = useAuth();
  const canEditTimes = ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'fabrication_manager', 'foreman', 'shop_foreman', 'millwright_foreman'].includes(user?.role) || (user?.extra_permissions || []).includes('field_review');
  const canViewAudit = ['super_admin', 'admin', 'office_admin'].includes(user?.role);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const openAudit = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const r = await axios.get(`${API}/timesheet/audit-log`, { params: { limit: 300 } });
      setAuditLogs(r.data.logs || []);
    } catch { toast.error('Failed to load change log'); }
    setAuditLoading(false);
  };
  // ?week=YYYY-MM-DD (any day → that Sun–Sat week) & ?wo=26-7051 deep-link from the Schedule's timesheet reconciliation
  const linkWeek = urlParams().get('week');
  const linkWo = urlParams().get('wo');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = linkWeek ? new Date(linkWeek + 'T00:00:00') : new Date();
    d.setDate(d.getDate() - d.getDay());
    return localDate(d);
  });
  const [dateTo, setDateTo] = useState(() => {
    const d = linkWeek ? new Date(linkWeek + 'T00:00:00') : new Date();
    d.setDate(d.getDate() - d.getDay() + 6);
    return localDate(d);
  });

  // Pick any day → snap the range to that whole week (Sunday → Saturday)
  const selectWeekFrom = (val) => {
    if (!val) { setDateFrom(val); return; }
    const d = new Date(val + 'T00:00:00');
    d.setDate(d.getDate() - d.getDay());        // back up to Sunday
    const sat = new Date(d); sat.setDate(d.getDate() + 6);
    setDateFrom(localDate(d));
    setDateTo(localDate(sat));
  };
  const [rawData, setRawData] = useState(null);
  // PM filter: "print just Carl's timesheets" — a view over the week, so Print / Send to printer / totals follow it.
  const [pmFilter, setPmFilter] = useState('');
  const pmOptions = useMemo(() => {
    const s = new Set((rawData?.work_orders || []).map(w => (w.pm || '').trim()).filter(Boolean));
    return [...s].sort();
  }, [rawData]);
  const data = useMemo(() => {
    if (!rawData || !pmFilter) return rawData;
    const wos = rawData.work_orders.filter(w => pmFilter === '__none__' ? !(w.pm || '').trim() : (w.pm || '').trim() === pmFilter);
    return { ...rawData, work_orders: wos, grand_total_hours: Math.round(wos.reduce((a, w) => a + (w.total_hours || 0), 0) * 100) / 100 };
  }, [rawData, pmFilter]);
  const setData = useCallback((v) => setRawData(prev => (typeof v === 'function' ? v(prev) : v)), []);
  useEffect(() => {
    if (!linkWo || !data) return;
    const el = document.getElementById(`wo-section-${linkWo}`);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); el.classList.add('ring-2', 'ring-emerald-400'); }
    // eslint-disable-next-line
  }, [data]);
  const [loading, setLoading] = useState(false);
  const [showSigPad, setShowSigPad] = useState(false);
  const [customerSig, setCustomerSig] = useState(null);
  const sigRef = React.useRef(null);
  const [woEdit, setWoEdit] = useState(null); // { key, oldWo, empName, empId, value }
  const [woEditResults, setWoEditResults] = useState([]);
  const [cellEdit, setCellEdit] = useState(null); // { rowKey, wo, date, label, hours, woValue, reason, status }
  const [cellWoResults, setCellWoResults] = useState([]);
  const [cellSaving, setCellSaving] = useState(false);
  const [travelEdit, setTravelEdit] = useState(null); // { key, wo, empName, empId, days:[{date,label,hours,travel}], values:{date: str} }
  const [travelSaving, setTravelSaving] = useState(false);
  const [histTip, setHistTip] = useState(null); // { x, y, emp, label, items }
  const [itemsWo, setItemsWo] = useState(null); // WO number string for the Add Items modal
  const [itemDate, setItemDate] = useState('');

  const openItemsModal = (woNumber) => {
    const todayStr = today();
    setItemDate((todayStr >= dateFrom && todayStr <= dateTo) ? todayStr : dateFrom);
    setItemsWo(woNumber);
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Remove this item from the timesheet?')) return;
    try {
      await axios.delete(`${API}/timesheet-items/${id}`);
      toast.success('Item removed');
      generate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
  };

  const searchCellWo = async (q) => {
    setCellEdit(prev => prev ? { ...prev, woValue: q } : prev);
    if (!q || q.length < 2) { setCellWoResults([]); return; }
    try {
      const r = await axios.get(`${API}/work-orders`, { params: { q } });
      setCellWoResults((r.data || []).slice(0, 6));
    } catch { setCellWoResults([]); }
  };

  const saveCellEdit = async () => {
    if (!cellEdit || cellSaving) return;
    setCellSaving(true);
    try {
      await axios.put(`${API}/my-timesheet-cell`, {
        date: cellEdit.date,
        work_order_number: cellEdit.wo,
        hours: parseFloat(cellEdit.hours),
        new_work_order_number: (cellEdit.woValue || '').trim() || undefined,
        reason: (cellEdit.reason || '').trim(),
      });
      toast.success(cellEdit.status === 'approved'
        ? 'Updated — your PM has to re-approve this day'
        : 'Updated — your PM will see the change at approval');
      setCellEdit(null); setCellWoResults([]);
      generate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save your change'); }
    setCellSaving(false);
  };

  const searchWoEdit = async (q) => {
    setWoEdit(prev => prev ? { ...prev, value: q } : prev);
    if (!q || q.length < 2) { setWoEditResults([]); return; }
    try {
      const r = await axios.get(`${API}/work-orders`, { params: { q } });
      setWoEditResults((r.data || []).slice(0, 6));
    } catch { setWoEditResults([]); }
  };

  const saveTravelEdit = async () => {
    if (!travelEdit) return;
    const changes = travelEdit.days.filter(dd => {
      const want = parseFloat(travelEdit.values[dd.date]) || 0;
      return Math.abs(want - (dd.travel || 0)) > 0.001;
    });
    if (!changes.length) { setTravelEdit(null); return; }
    setTravelSaving(true);
    try {
      for (const dd of changes) {
        await axios.post(`${API}/set-travel-hours`, { employee_id: travelEdit.empId, date: dd.date, work_order_number: travelEdit.wo, travel_hours: parseFloat(travelEdit.values[dd.date]) || 0 });
      }
      toast.success(`Travel hours saved for ${travelEdit.empName} (${changes.length} day${changes.length > 1 ? 's' : ''})`);
      setTravelEdit(null);
      generate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save travel hours'); }
    setTravelSaving(false);
  };

  const saveWoReassign = async () => {
    if (!woEdit || !woEdit.value.trim()) { toast.error('Enter a work order number'); return; }
    if (woEdit.value.trim() === woEdit.oldWo) { setWoEdit(null); setWoEditResults([]); return; }
    const dayOptions = woEdit.dayOptions || [];
    const selected = woEdit.selectedDays || [];
    if (dayOptions.length > 0 && selected.length === 0) { toast.error('Pick at least one day to move'); return; }
    const partial = dayOptions.length > 0 && selected.length < dayOptions.length;
    try {
      const r = await axios.post(`${API}/timesheet/reassign-wo`, {
        employee_name: woEdit.empName,
        employee_id: woEdit.empId || undefined,
        old_wo: woEdit.oldWo,
        new_wo: woEdit.value.trim(),
        date_from: dateFrom,
        date_to: dateTo,
        ...(partial ? { dates: selected } : {}),
      });
      toast.success(`Moved ${woEdit.empName}${partial ? ` (${selected.length} day${selected.length === 1 ? '' : 's'})` : ''} to ${woEdit.value.trim()} (${r.data.modified} record${r.data.modified === 1 ? '' : 's'})`);
      setWoEdit(null); setWoEditResults([]);
      generate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reassign work order'); }
  };

  const deleteEmployeeRow = async (emp, woNumber) => {
    if (!window.confirm(`Delete ALL of ${emp.employee_name}'s hours on WO ${woNumber} for this week (${dateFrom} → ${dateTo})?\n\nThis fully removes the row — use it to clear a misspelled/duplicate name.`)) return;
    try {
      const r = await axios.post(`${API}/timesheet/delete-employee`, {
        employee_name: emp.employee_name,
        work_order_number: woNumber,
        date_from: dateFrom,
        date_to: dateTo,
      });
      toast.success(`Removed ${emp.employee_name} (${r.data.removed} record${r.data.removed === 1 ? '' : 's'})`);
      generate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete employee'); }
  };

  const [gridVersion, setGridVersion] = useState(0);
  // Day split across jobs → set how many REGULAR hours this row's job gets that day (the rest of it is OT);
  // the other jobs keep their current regular hours (squeezed if the day would pass 8h regular).
  const setDaySplitGrid = async (emp, date, woNumber, multi) => {
    if (!emp.employee_id) { toast.error('No account on this row — set the split in PM Review'); return; }
    const split = multi?.split || {};
    const cur = split[woNumber]?.reg ?? 0;
    const worked = (split[woNumber]?.reg ?? 0) + (split[woNumber]?.ot ?? 0);
    const ans = window.prompt(`${emp.employee_name} · ${date}\n${worked}h on ${woNumber} (${multi?.day_total}h day across ${multi?.jobs} jobs).\n\nRegular hours on ${woNumber}? The rest of it is overtime. 0 = all OT.`, String(cur));
    if (ans === null) return;
    const v = parseFloat(ans);
    if (Number.isNaN(v) || v < 0) { toast.error('Enter a number of regular hours'); return; }
    const pins = {};
    for (const [w, s] of Object.entries(split)) pins[w] = w === woNumber ? Math.min(v, worked) : s.reg;
    let over = Object.values(pins).reduce((a, b) => a + b, 0) - 8;
    for (const w of Object.keys(pins).reverse()) { if (w === woNumber || over <= 0) continue; const cut = Math.min(pins[w], over); pins[w] -= cut; over -= cut; }
    try {
      await axios.post(`${API}/set-day-split`, { employee_id: emp.employee_id, date, reg_by_wo: pins });
      toast.success(`${emp.employee_name}: ${Object.entries(pins).map(([w, h]) => `${w} ${h}h reg`).join(' · ')} — the rest is OT`);
      generate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not set the split'); }
  };
  const generate = useCallback(async () => {
    if (!dateFrom || !dateTo) return null;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/weekly-summary`, { params: { date_from: dateFrom, date_to: dateTo } });
      setData(r.data);
      setGridVersion(v => v + 1);
      setLoading(false);
      return r.data;
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load the week'); }
    setLoading(false);
    return null;
  }, [dateFrom, dateTo]);

  // Loads automatically — on open (current week) and whenever the dates change. No Generate button.
  useEffect(() => { generate(); }, [generate]);

  const shiftWeek = (days) => {
    const f = new Date(dateFrom + 'T00:00:00'); f.setDate(f.getDate() + days);
    const t = new Date(dateTo + 'T00:00:00'); t.setDate(t.getDate() + days);
    setDateFrom(localDate(f)); setDateTo(localDate(t));
  };

  const handlePrint = async () => {
    // Open the print window synchronously (avoids popup blockers) before the await.
    const pw = window.open('', '_blank');
    let numbers = {};
    try {
      const r = await axios.post(`${API}/saved-timesheets/save`, {
        date_from: dateFrom, date_to: dateTo,
        work_orders: data.work_orders, dates: data.dates, labour_rates: data.labour_rates, billing_rates: data.billing_rates,
      });
      numbers = r.data.numbers || {};
    } catch { toast.error('Could not assign timesheet numbers — printing without them'); }
    printTimesheet(data, dateFrom, dateTo, customerSig, numbers, pw);
  };

  // "Send to office printer": same sheet → PDF on the server → emailed to the MFP's print mailbox.
  const [remotePrinting, setRemotePrinting] = useState(false);
  const sendToPrinter = async (tsData, from, to, sig, numbers, label) => {
    if (remotePrinting) return;
    setRemotePrinting(true);
    try {
      const html = timesheetHtml(tsData, from, to, sig, numbers);
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/remote-print/timesheet`, { html, label });
      toast.success(`Sent to the office printer — it picks up mail every ~${r.data.poll_minutes} min`, { duration: 7000 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send to the printer'); }
    finally { setRemotePrinting(false); }
  };
  const handleRemotePrint = async () => {
    let numbers = {};
    try {
      const r = await axios.post(`${API}/saved-timesheets/save`, {
        date_from: dateFrom, date_to: dateTo,
        work_orders: data.work_orders, dates: data.dates, labour_rates: data.labour_rates, billing_rates: data.billing_rates,
      });
      numbers = r.data.numbers || {};
    } catch { toast.error('Could not assign timesheet numbers — sending without them'); }
    const wos = data.work_orders.map(w => w.work_order_number).join(', ');
    await sendToPrinter(data, dateFrom, dateTo, customerSig, numbers, `Timesheet ${wos} ${dateFrom} to ${dateTo}`);
  };

  // One sheet only (reprint a single WO without the rest of the week)
  const oneSheetData = (wo) => ({ ...data, work_orders: [wo], grand_total_hours: wo.total_hours || 0 });
  const numbersForOne = async (wo) => {
    try {
      const r = await axios.post(`${API}/saved-timesheets/save`, {
        date_from: dateFrom, date_to: dateTo, work_orders: [wo], dates: data.dates, labour_rates: data.labour_rates, billing_rates: data.billing_rates,
      });
      return r.data.numbers || {};
    } catch { toast.error('Could not assign a timesheet number — printing without it'); return {}; }
  };
  const printOne = async (wo) => {
    const pw = window.open('', '_blank');
    const numbers = await numbersForOne(wo);
    printTimesheet(oneSheetData(wo), dateFrom, dateTo, customerSig, numbers, pw);
  };
  const remotePrintOne = async (wo) => {
    const numbers = await numbersForOne(wo);
    await sendToPrinter(oneSheetData(wo), dateFrom, dateTo, customerSig, numbers, `Timesheet ${wo.work_order_number} ${dateFrom} to ${dateTo}`);
  };

  const [tsSearch, setTsSearch] = useState('');
  const [tsResults, setTsResults] = useState(null);
  const runTsSearch = async () => {
    try {
      const r = await axios.get(`${API}/saved-timesheets/search`, { params: { q: tsSearch.trim() } });
      setTsResults(r.data || []);
    } catch { toast.error('Search failed'); setTsResults([]); }
  };
  const printSaved = (row) => {
    const snap = row.snapshot || {};
    const savedData = {
      dates: snap.dates || [],
      labour_rates: snap.labour_rates || {},
      billing_rates: snap.billing_rates || null,
      work_orders: snap.wo ? [snap.wo] : [],
      grand_total_hours: row.total_hours || 0,
    };
    const pw = window.open('', '_blank');
    printTimesheet(savedData, row.date_from, row.date_to, null, { [row.work_order_number]: row.timesheet_number }, pw);
  };
  const remotePrintSaved = (row) => {
    const snap = row.snapshot || {};
    const savedData = { dates: snap.dates || [], labour_rates: snap.labour_rates || {}, billing_rates: snap.billing_rates || null,
      work_orders: snap.wo ? [snap.wo] : [], grand_total_hours: row.total_hours || 0 };
    sendToPrinter(savedData, row.date_from, row.date_to, null, { [row.work_order_number]: row.timesheet_number },
      `Timesheet ${row.timesheet_number} ${row.work_order_number}`);
  };

  const [distEdit, setDistEdit] = useState(null); // { wo, unit: 'km'|'hours', value, mode }
  const TRAVEL_MODE_LABEL = { standard: 'Crew 1-way · Foreman 2-way', one_way: '1-WAY (everyone)', two_way: '2-WAY (everyone)' };
  const openDistEdit = (wo) => setDistEdit({
    wo: wo.work_order_number,
    unit: wo.travel?.hours_source === 'manual' ? 'hours' : 'km',
    value: wo.travel?.hours_source === 'manual' ? (wo.travel?.band_hours ?? '') : (wo.travel?.source === 'manual' ? (wo.travel?.distance_km ?? '') : ''),
    mode: wo.travel?.travel_mode || (wo.travel?.one_way_only ? 'one_way' : 'standard'),
  });
  const saveDistance = async (wo, edit, clear = false) => {
    const value = clear ? '' : edit.value;
    const isHours = edit.unit === 'hours';
    try {
      await axios.post(`${API}/timesheet/set-distance`, {
        work_order_number: wo,
        distance_km: (!isHours && value !== '') ? parseFloat(value) : null,
        travel_hours: (isHours && value !== '') ? parseFloat(value) : null,
        travel_mode: edit.mode || 'standard',
      });
      toast.success(clear
        ? `${wo}: travel back to GPS distance${edit.mode !== 'standard' ? ` (${TRAVEL_MODE_LABEL[edit.mode]})` : ''}`
        : `${wo}: travel set — ${value !== '' ? (isHours ? `${value}h/way` : `${value} km`) + ' · ' : ''}${TRAVEL_MODE_LABEL[edit.mode || 'standard']}`);
      setDistEdit(null);
      generate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to set travel'); }
  };

  const [rateEdit, setRateEdit] = useState(null); // { wo, value }
  const saveRate = async (wo, value) => {
    try {
      await axios.post(`${API}/timesheet/set-rate`, {
        work_order_number: wo, rate: value === '' ? null : parseFloat(value),
      });
      toast.success(value === '' ? `${wo}: back to standard rates` : `${wo}: rate set to $${value}/hr (OT $${(parseFloat(value) * 1.5).toFixed(2)})`);
      setRateEdit(null);
      generate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to set rate'); }
  };

  const saveBillingType = async (wo, billingType) => {
    try {
      await axios.post(`${API}/timesheet/set-billing-type`, { work_order_number: wo, billing_type: billingType });
      generate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to set billing type'); }
  };

  const [truckEdit, setTruckEdit] = useState(null); // { wo, value }
  const saveTrucks = async (wo, value) => {
    try {
      await axios.post(`${API}/timesheet/set-trucks`, {
        work_order_number: wo, trucks_per_day: value === '' ? null : parseInt(value, 10),
      });
      toast.success(value === '' ? `${wo}: back to automatic truck count` : `${wo}: ${value} service vehicle${Number(value) === 1 ? '' : 's'}/day`);
      setTruckEdit(null);
      generate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to set truck count'); }
  };

  useEffect(() => { generate(); }, []);

  const dayLabels = (data?.dates || []).map(d => {
    const dt = new Date(d + 'T00:00:00');
    return { date: d, label: dt.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' }) };
  });

  return (
    <div className="space-y-4">
      {histTip && (
        <div data-testid="ts-cell-history-tip"
          className="fixed z-[60] w-72 bg-white border border-slate-200 rounded-lg shadow-2xl p-2.5 text-[11px] pointer-events-none"
          style={{ left: Math.max(8, Math.min(histTip.x - 144, window.innerWidth - 296)), top: histTip.y + 6 }}>
          <p className="text-slate-500 font-semibold mb-1">{histTip.emp} — {histTip.label} · change log</p>
          <div className="space-y-1.5 max-h-56 overflow-hidden">
            {histTip.items.map((h, i) => (
              <div key={i} className="border-l-2 pl-2" style={{ borderColor: h.kind === 'manager' ? '#38bdf8' : '#c084fc' }}>
                <p className="text-slate-900">
                  {h.from_hours != null ? `${h.from_hours}h` : '—'} → <b>{h.to_hours != null ? `${h.to_hours}h` : '—'}</b>
                  {h.from_wo && h.to_wo && h.from_wo !== h.to_wo && <span className="text-slate-700"> · {h.from_wo} → {h.to_wo}</span>}
                </p>
                <p className="text-slate-500">
                  {h.kind === 'manager' ? 'PM/manager edit' : 'Self-edit'} · {h.by || 'Unknown'}{h.at ? ` · ${new Date(h.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Toronto' })}` : ''}
                </p>
                {h.reason && <p className="text-purple-700 italic">"{h.reason}"</p>}
                {h.was_approved && <p className="text-amber-700">was approved → back to pending</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => shiftWeek(-7)} title="Previous week" data-testid="field-week-prev"
          className="px-2.5 py-2 bg-white hover:bg-slate-100 border border-slate-300 text-slate-800 rounded-lg text-sm">‹</button>
        <input type="date" value={dateFrom} onChange={e => selectWeekFrom(e.target.value)}
          title="Pick any day — the week (Sun–Sat) loads automatically"
          data-testid="field-week-start" className="px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
        <span className="text-slate-500">to</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          data-testid="field-week-end" className="px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
        <button onClick={() => shiftWeek(7)} title="Next week" data-testid="field-week-next"
          className="px-2.5 py-2 bg-white hover:bg-slate-100 border border-slate-300 text-slate-800 rounded-lg text-sm">›</button>
        <button onClick={generate} disabled={loading} title="Reload this week" data-testid="field-week-refresh"
          className="p-2 bg-white hover:bg-slate-100 border border-slate-300 text-slate-800 rounded-lg disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {loading && !data && <span className="text-xs text-slate-500">Loading week…</span>}
        {rawData && rawData.work_orders.length > 0 && pmOptions.length > 0 && (
          <select value={pmFilter} onChange={e => setPmFilter(e.target.value)} data-testid="ts-pm-filter"
            title="Show (and print) only one PM's timesheets"
            className={`px-3 py-2 rounded-lg text-sm font-medium border outline-none ${pmFilter ? 'bg-amber-100 border-amber-400 text-amber-900' : 'bg-white/90 border-slate-300 text-slate-700'}`}>
            <option value="">All PMs ({rawData.work_orders.length})</option>
            {pmOptions.map(pm => <option key={pm} value={pm}>{pm} ({rawData.work_orders.filter(w => (w.pm || '').trim() === pm).length})</option>)}
            {rawData.work_orders.some(w => !(w.pm || '').trim()) && <option value="__none__">No PM set ({rawData.work_orders.filter(w => !(w.pm || '').trim()).length})</option>}
          </select>
        )}
        {data && data.work_orders.length > 0 && (
          <>
          <button onClick={handlePrint} data-testid="ts-print-btn"
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-lg text-sm font-medium flex items-center gap-1">
            <FileText className="w-3.5 h-3.5" /> Print
          </button>
          <button onClick={handleRemotePrint} disabled={remotePrinting} data-testid="ts-remote-print-btn"
            title="Prints at the office — the sheet is emailed to the printer's mailbox"
            className="px-4 py-2 bg-indigo-700 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-1">
            <Printer className="w-3.5 h-3.5" /> {remotePrinting ? 'Sending…' : 'Send to office printer'}
          </button>
          <button onClick={() => setShowSigPad(!showSigPad)}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 ${customerSig ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}`}>
            <Edit2 className="w-3.5 h-3.5" /> {customerSig ? 'Signature Captured' : 'Customer Signature'}
          </button>
          </>
        )}
        {canViewAudit && (
          <button onClick={openAudit} data-testid="ts-change-log-btn"
            className="px-4 py-2 bg-white/90 hover:bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium flex items-center gap-1 ml-auto">
            <History className="w-3.5 h-3.5" /> Change Log
          </button>
        )}
      </div>

      {/* Find a saved timesheet by its number */}
      <div className="flex items-center gap-2 flex-wrap">
        <Search className="w-4 h-4 text-slate-500" />
        <input value={tsSearch} onChange={e => setTsSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') runTsSearch(); }}
          placeholder="Find a saved timesheet (e.g. TS-1001 or work order #)"
          data-testid="ts-search-input"
          className="px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none w-72" />
        <button onClick={runTsSearch} data-testid="ts-search-btn"
          className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-lg text-sm font-medium">Search</button>
        {tsResults !== null && (
          <button onClick={() => { setTsResults(null); setTsSearch(''); }}
            className="px-3 py-2 text-slate-700 hover:text-slate-900 text-sm">Clear</button>
        )}
      </div>

      {tsResults !== null && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="ts-search-results">
          {tsResults.length === 0 ? (
            <p className="px-4 py-4 text-slate-500 text-sm text-center">No saved timesheets found</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="px-4 py-2 text-left">Timesheet #</th>
                  <th className="px-4 py-2 text-left">Work Order</th>
                  <th className="px-4 py-2 text-left">Week</th>
                  <th className="px-4 py-2 text-right">Hours</th>
                  <th className="px-4 py-2 text-right">Saved</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {tsResults.map(row => (
                  <tr key={row.timesheet_number} className="text-slate-800" data-testid={`ts-result-${row.timesheet_number}`}>
                    <td className="px-4 py-2 font-mono font-bold text-red-600">{row.timesheet_number}</td>
                    <td className="px-4 py-2 font-mono text-blue-700">{row.work_order_number}</td>
                    <td className="px-4 py-2 text-slate-500">{row.date_from} → {row.date_to}</td>
                    <td className="px-4 py-2 text-right">{row.total_hours}h</td>
                    <td className="px-4 py-2 text-right text-slate-500 text-xs">{(row.created_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => printSaved(row)} data-testid={`ts-print-saved-${row.timesheet_number}`}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium inline-flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Print
                      </button>
                      <button onClick={() => remotePrintSaved(row)} disabled={remotePrinting} data-testid={`ts-remote-print-saved-${row.timesheet_number}`}
                        title="Prints at the office"
                        className="ml-1 px-3 py-1 bg-indigo-700 hover:bg-indigo-600 disabled:opacity-50 text-white rounded text-xs font-medium inline-flex items-center gap-1">
                        <Printer className="w-3 h-3" /> Office
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Customer Signature Pad */}
      {showSigPad && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-medium text-slate-900 mb-2">Customer Signature</h3>
          <div className="bg-white rounded-lg" style={{width:'100%',maxWidth:'500px',height:'150px'}}>
            <SignatureCanvas ref={sigRef} penColor="black"
              canvasProps={{width: 500, height: 150, className: 'rounded-lg', style:{width:'100%',height:'150px'}}} />
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={() => {
              if (sigRef.current && !sigRef.current.isEmpty()) {
                setCustomerSig(sigRef.current.toDataURL('image/png'));
                setShowSigPad(false);
                toast.success('Signature captured');
              } else { toast.error('Please sign first'); }
            }} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium">
              Save Signature
            </button>
            <button onClick={() => sigRef.current?.clear()}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-lg text-sm font-medium">Clear</button>
            <button onClick={() => setShowSigPad(false)}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-lg text-sm font-medium">Cancel</button>
          </div>
          {customerSig && (
            <div className="mt-2">
              <p className="text-xs text-slate-500 mb-1">Current signature:</p>
              <img src={customerSig} alt="sig" className="h-10 bg-white rounded px-2" />
            </div>
          )}
        </div>
      )}

      {data && data.work_orders.length === 0 && (
        <div className="text-center py-12 text-slate-500">No field time entries for this period</div>
      )}

      {data?.absences?.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-3" data-testid="ts-absences-strip">
          <p className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-1.5">Sick / Vacation this week</p>
          <div className="flex flex-wrap gap-1.5">
            {data.absences.map((a, i) => (
              <span key={i} className="text-xs px-2 py-0.5 bg-white rounded border border-amber-200 text-slate-800">
                <b>{a.employee_name}</b> — {a.summary}
              </span>
            ))}
          </div>
        </div>
      )}

      {data && data.work_orders.map(wo => (
        <div key={wo.work_order_number} id={`wo-section-${wo.work_order_number}`} className={`bg-white rounded-xl border overflow-x-auto ${!wo.billing_type ? 'border-amber-400 ring-2 ring-amber-300' : 'border-slate-300'}`}>
          <div className={`px-4 py-2.5 border-b flex items-center justify-between gap-3 flex-wrap ${!wo.billing_type ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-300'}`}>
            <div>
              <span className="font-mono text-blue-600 font-bold">{wo.work_order_number}</span>
              {canEditTimes && <SheetWoEditor wo={wo} dateFrom={dateFrom} dateTo={dateTo} onMoved={() => generate()} />}
              {wo.customer_name && <span className="text-slate-700 font-medium ml-2" data-testid={`ts-customer-${wo.work_order_number}`}>{wo.customer_name}</span>}
              {wo.pm && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px] font-medium" title="Project manager" data-testid={`ts-pm-${wo.work_order_number}`}>PM {wo.pm}</span>}
              {wo.po_number && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-300 text-emerald-800 text-xs font-mono font-semibold"
                  title={`Customer PO from the Work Orders sheet (column J)${wo.po_history?.length ? ` — most recent of ${wo.po_history.length + 1}: earlier ${wo.po_history.join(', ')}` : ''}`}
                  data-testid={`ts-po-${wo.work_order_number}`}>
                  PO {wo.po_number}{wo.po_history?.length > 0 && <span className="text-[9px] font-sans text-emerald-600">latest of {wo.po_history.length + 1}</span>}
                </span>
              )}
              <span className="text-slate-900 font-semibold ml-2">{wo.total_hours}h total</span>
              {wo.shop_hours > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded bg-orange-50 border border-orange-300 text-orange-800 text-[11px] font-semibold" title="Shop fabrication hours on this job (Shop Tracker) — billed at the FAB rate" data-testid={`ts-shop-hours-${wo.work_order_number}`}>
                  incl. {wo.shop_hours}h FAB
                </span>
              )}
              {!wo.billing_type && (
                <span data-testid={`ts-billing-warn-${wo.work_order_number}`}
                  className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wide animate-pulse">
                  <AlertTriangle className="w-3 h-3" /> Needs T&amp;M / Costing
                </span>
              )}
            </div>
            <div className="text-xs">
              {distEdit && distEdit.wo === wo.work_order_number ? (
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="ts-travel-editor">
                  <span className="text-slate-500">Travel:</span>
                  <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" title="Enter the distance (hours/way come from the km bands) or the travel hours per way directly">
                    <button onClick={() => setDistEdit({ ...distEdit, unit: 'km', value: '' })} data-testid="ts-travel-unit-km"
                      className={`px-2 py-1 font-semibold ${distEdit.unit === 'km' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}>km</button>
                    <button onClick={() => setDistEdit({ ...distEdit, unit: 'hours', value: '' })} data-testid="ts-travel-unit-hours"
                      className={`px-2 py-1 font-semibold border-l border-slate-300 ${distEdit.unit === 'hours' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}>hrs</button>
                  </div>
                  <input type="number" autoFocus value={distEdit.value} step={distEdit.unit === 'hours' ? '0.25' : '1'} min="0"
                    onChange={e => setDistEdit({ ...distEdit, value: e.target.value })}
                    placeholder={distEdit.unit === 'hours' ? 'hrs / way' : 'km'} data-testid="ts-dist-input"
                    className="w-20 px-2 py-1 border border-slate-300 rounded text-slate-900 outline-none" />
                  <select value={distEdit.mode} onChange={e => setDistEdit({ ...distEdit, mode: e.target.value })} data-testid="ts-travel-mode"
                    className="px-2 py-1 border border-slate-300 rounded text-slate-900 bg-white outline-none">
                    <option value="standard">Crew 1-way · Foreman 2-way</option>
                    <option value="one_way">1-way — everyone</option>
                    <option value="two_way">2-way — everyone</option>
                  </select>
                  <button onClick={() => saveDistance(wo.work_order_number, distEdit)} data-testid="ts-dist-save"
                    className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded">Save</button>
                  <button onClick={() => saveDistance(wo.work_order_number, distEdit, true)} title="Clear the km / hours override — use the GPS distance (keeps the 1-way / 2-way choice)"
                    data-testid="ts-dist-gps"
                    className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded">Use GPS</button>
                  <button onClick={() => setDistEdit(null)} className="px-2 py-1 text-slate-500 hover:text-slate-800">✕</button>
                </div>
              ) : (
                <button onClick={() => openDistEdit(wo)}
                  data-testid={`ts-dist-${wo.work_order_number}`}
                  className="inline-flex items-center gap-1 text-slate-600 hover:text-blue-600">
                  <MapPin className="w-3.5 h-3.5" />
                  {(() => {
                    const t = wo.travel || {};
                    const mode = t.travel_mode || (t.one_way_only ? 'one_way' : 'standard');
                    const modeTag = mode === 'one_way' ? ' · 1-WAY ALL' : mode === 'two_way' ? ' · 2-WAY ALL' : '';
                    if (t.hours_source === 'manual') return `Travel ${t.band_hours}h/way (set)${t.distance_km != null ? ` · ${t.distance_km} km` : ''}${modeTag}`;
                    if (t.distance_km != null) return `${t.distance_km} km${t.source === 'manual' ? ' (set)' : ' (GPS)'} · Travel ${t.band_hours}h/way${modeTag}`;
                    return `Set travel (km or hrs)${modeTag}`;
                  })()}
                </button>
              )}
            </div>
            {canEditTimes && (
            <div className="text-xs">
              {rateEdit && rateEdit.wo === wo.work_order_number ? (
                <div className="flex items-center gap-1">
                  <span className="text-slate-500">Rate ($/hr):</span>
                  <input type="number" step="0.01" autoFocus value={rateEdit.value}
                    onChange={e => setRateEdit({ wo: wo.work_order_number, value: e.target.value })}
                    placeholder="$/hr" data-testid="ts-rate-input"
                    className="w-20 px-2 py-1 border border-slate-300 rounded text-slate-900 outline-none" />
                  <button onClick={() => saveRate(wo.work_order_number, rateEdit.value)} data-testid="ts-rate-save"
                    className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded">Save</button>
                  <button onClick={() => saveRate(wo.work_order_number, '')} title="Clear override — use standard rates"
                    className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded">Standard</button>
                  <button onClick={() => setRateEdit(null)} className="px-2 py-1 text-slate-500 hover:text-slate-800">✕</button>
                </div>
              ) : (
                <button onClick={() => setRateEdit({ wo: wo.work_order_number, value: wo.rate_override ?? '' })}
                  data-testid={`ts-rate-${wo.work_order_number}`}
                  className="inline-flex items-center gap-1 text-slate-600 hover:text-emerald-600">
                  <DollarSign className="w-3.5 h-3.5" />
                  {wo.rate_override != null
                    ? `$${Number(wo.rate_override).toFixed(2)}/hr flat (OT $${(Number(wo.rate_override) * 1.5).toFixed(2)})`
                    : 'Set job rate'}
                </button>
              )}
            </div>
            )}
            {canEditTimes && (
            <div className="text-xs">
              {truckEdit && truckEdit.wo === wo.work_order_number ? (
                <div className="flex items-center gap-1">
                  <span className="text-slate-500">Trucks/day:</span>
                  <input type="number" min="0" max="20" step="1" autoFocus value={truckEdit.value}
                    onChange={e => setTruckEdit({ wo: wo.work_order_number, value: e.target.value })}
                    placeholder="#" data-testid="ts-trucks-input"
                    className="w-14 px-2 py-1 border border-slate-300 rounded text-slate-900 outline-none" />
                  <button onClick={() => saveTrucks(wo.work_order_number, truckEdit.value)} data-testid="ts-trucks-save"
                    className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded">Save</button>
                  <button onClick={() => saveTrucks(wo.work_order_number, '')} title="Clear override — count automatically (1 per truck holder, or 1 per 4 crew)"
                    className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded">Auto</button>
                  <button onClick={() => setTruckEdit(null)} className="px-2 py-1 text-slate-500 hover:text-slate-800">✕</button>
                </div>
              ) : (
                <button onClick={() => setTruckEdit({ wo: wo.work_order_number, value: wo.truck_override ?? '' })}
                  data-testid={`ts-trucks-${wo.work_order_number}`}
                  title="Service vehicles billed per on-site day — override when crews share a truck"
                  className="inline-flex items-center gap-1 text-slate-600 hover:text-orange-600">
                  <Truck className="w-3.5 h-3.5" />
                  {wo.truck_override != null
                    ? `${wo.truck_override} truck${Number(wo.truck_override) === 1 ? '' : 's'}/day (set)`
                    : 'Auto trucks'}
                </button>
              )}
            </div>
            )}
            {canEditTimes && (
            <div className="text-xs flex items-center gap-1">
              <span className="text-slate-500">Billing:</span>
              <button onClick={() => saveBillingType(wo.work_order_number, wo.billing_type === 'tm' ? null : 'tm')}
                data-testid={`ts-billing-tm-${wo.work_order_number}`}
                className={`px-2 py-1 rounded font-semibold transition-colors ${wo.billing_type === 'tm' ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>T&amp;M</button>
              <button onClick={() => saveBillingType(wo.work_order_number, wo.billing_type === 'costing' ? null : 'costing')}
                data-testid={`ts-billing-costing-${wo.work_order_number}`}
                className={`px-2 py-1 rounded font-semibold transition-colors ${wo.billing_type === 'costing' ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>Costing</button>
            </div>
            )}
            <div className="text-xs flex items-center gap-1 ml-auto">
              <button onClick={() => printOne(wo)} data-testid={`ts-print-one-${wo.work_order_number}`} title="Print just this timesheet"
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white text-slate-900 hover:bg-slate-100 font-semibold">
                <Printer className="w-3.5 h-3.5" /> Print this sheet
              </button>
              <button onClick={() => remotePrintOne(wo)} disabled={remotePrinting} data-testid={`ts-remote-print-one-${wo.work_order_number}`} title="Send just this timesheet to the office printer"
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-200 text-slate-700 hover:bg-slate-300 font-semibold disabled:opacity-50">
                <Printer className="w-3.5 h-3.5" /> Office
              </button>
            </div>
          </div>
          <ServiceDescriptionBox wo={wo} dateFrom={dateFrom} dateTo={dateTo} onSaved={(txt) => setData(d => ({ ...d, work_orders: d.work_orders.map(w => w.work_order_number === wo.work_order_number ? { ...w, service_description: txt } : w) }))} />
          <table className="w-full text-xs whitespace-nowrap border-collapse">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                <th className="px-3 py-1.5 text-left border border-slate-300 sticky left-0 bg-slate-100 z-10">Employee</th>
                {dayLabels.map(d => (
                  <th key={d.date} className="px-2 py-1.5 text-center border border-slate-300">{d.label}</th>
                ))}
                <th className="px-3 py-1.5 text-center border border-slate-300 font-bold bg-yellow-50">Total</th>
              </tr>
            </thead>
            <tbody>
              {wo.employees.map((emp, i) => {
                const rowKey = `${wo.work_order_number}|${emp.employee_name}${emp.is_fab ? '|FAB' : ''}`;
                const editingWo = woEdit && woEdit.key === rowKey;
                const fabTitle = 'Shop fabrication hours from the Shop Tracker — billed at the FAB rate. Edit them in the Shop Tracker.';
                return (
                <React.Fragment key={`${emp.employee_name}${emp.is_fab ? '|FAB' : ''}`}>
                <tr className={i % 2 === 0 ? 'bg-blue-50' : ''}>
                  <td className={`px-3 py-1 text-slate-900 font-medium border border-slate-300 sticky left-0 z-10 ${i % 2 === 0 ? 'bg-blue-50' : 'bg-white'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span>{emp.employee_name}{emp.is_fab && <span className="ml-1.5 text-[9px] font-bold text-orange-800 bg-orange-50 border border-orange-300 rounded px-1 align-middle" title={fabTitle} data-testid={`ts-fab-badge-${emp.employee_name.replace(/\s+/g, '-').toLowerCase()}`}>FAB</span>}</span>
                      {canEditTimes && !emp.is_fab && (
                      <div className="flex items-center gap-1 shrink-0">
                      <button
                        title="Correct work order"
                        data-testid={`ts-edit-wo-${emp.employee_name.replace(/\s+/g, '-').toLowerCase()}`}
                        onClick={() => {
                          const dayOptions = dayLabels
                            .filter(dl => emp.hours_by_day[dl.date])
                            .map(dl => ({ date: dl.date, label: dl.label, hours: emp.hours_by_day[dl.date] }));
                          setWoEdit({ key: rowKey, oldWo: wo.work_order_number, empName: emp.employee_name, empId: emp.employee_id, value: '', dayOptions, selectedDays: dayOptions.map(o => o.date) });
                          setWoEditResults([]);
                        }}
                        className="text-slate-500 hover:text-blue-600">
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        title="Travel hours — mark part or all of a day as travel (travel rate, no OT)"
                        data-testid={`ts-day-travel-${emp.employee_name.replace(/\s+/g, '-').toLowerCase()}`}
                        onClick={() => {
                          if (travelEdit?.key === rowKey) { setTravelEdit(null); return; }
                          const days = dayLabels.filter(dl => emp.hours_by_day[dl.date]).map(dl => ({ date: dl.date, label: dl.label, hours: emp.hours_by_day[dl.date], travel: emp.travel_by_day?.[dl.date] || 0 }));
                          setTravelEdit({ key: rowKey, wo: wo.work_order_number, empName: emp.employee_name, empId: emp.employee_id, days, values: Object.fromEntries(days.map(x => [x.date, x.travel ? String(x.travel) : ''])) });
                        }}
                        className={`hover:text-sky-700 ${emp.travel_total > 0 ? 'text-sky-600' : 'text-slate-500'}`}>
                        <Plane className="w-3 h-3" />
                      </button>
                      <button
                        title="Delete this person from this work order"
                        data-testid={`ts-delete-emp-${emp.employee_name.replace(/\s+/g, '-').toLowerCase()}`}
                        onClick={() => deleteEmployeeRow(emp, wo.work_order_number)}
                        className="text-slate-500 hover:text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </button>
                      </div>
                      )}
                    </div>
                  </td>
                  {dayLabels.map(d => {
                    const val = emp.hours_by_day[d.date] || '';
                    const att = !val && emp.attendance_by_day?.[d.date];
                    const meta = emp.cells?.[d.date];
                    const isOwnCell = !canEditTimes && !!emp.employee_id && user?.id === emp.employee_id;
                    const aftDay = emp.afternoon_by_day?.[d.date];
                    const otModeRaw = emp.ot_by_day?.[d.date];
                    const travDay = emp.travel_by_day?.[d.date] || 0;
                    const otMode = otModeRaw === 'travel' ? null : otModeRaw;
                    const multi = emp.multi_job_by_day?.[d.date];
                    const mySplit = multi?.split?.[wo.work_order_number];
                    const multiHere = multi && ((mySplit ? mySplit.ot > 0 : (multi.ot_job === wo.work_order_number && multi.ot_hours > 0)));
                    const myOt = mySplit ? mySplit.ot : multi?.ot_hours;
                    const dayMarkers = (aftDay || otMode || travDay || multi) ? (
                      <span className="block text-[8px] leading-none font-bold mt-0.5">
                        {travDay > 0 && <span className="text-sky-700" title={travDay >= val ? 'All travel — billed at the travel rate, no reg/OT' : `${travDay}h of ${val}h are travel (travel rate, no OT)`} data-testid={`ts-trv-${emp.employee_name.replace(/\s+/g, '-').toLowerCase()}-${d.date}`}>{travDay >= val ? 'TRV' : `${travDay} TRV`}</span>}
                        {travDay > 0 && (aftDay || otMode) && ' '}
                        {aftDay && <span className="text-orange-600" title="Afternoon shift — $2/hr premium">AFT</span>}
                        {aftDay && otMode && ' '}
                        {otMode && (
                          <span
                            className={otMode === 'all_reg' ? 'text-emerald-600' : (otMode === 'all_dt' || otMode === 'mixed_dt') ? 'text-rose-600' : 'text-amber-600'}
                            data-testid={(otMode === 'all_dt' || otMode === 'mixed_dt') ? `ts-dt-${emp.employee_name.replace(/\s+/g, '-').toLowerCase()}-${d.date}` : undefined}
                            title={otMode === 'all_dt' ? `Double time (2×) — ${DT_LABEL[emp.dt_reason_by_day?.[d.date]] || 'stat holiday / breakdown'}`
                              : otMode === 'mixed_dt' ? `${emp.reg_ot_by_day?.[d.date]?.dt ?? '?'}h at double time (${DT_LABEL[emp.dt_reason_by_day?.[d.date]] || ''})`
                              : otMode === 'all_ot' ? 'Approved as ALL overtime'
                              : otMode === 'all_reg' ? 'Approved as all regular'
                              : `Split day: ${emp.reg_ot_by_day?.[d.date]?.reg ?? '?'}h reg + ${emp.reg_ot_by_day?.[d.date]?.ot ?? '?'}h OT`}
                          >
                            {otMode === 'all_dt' ? '2× DT' : otMode === 'mixed_dt' ? 'DT+' : otMode === 'all_ot' ? 'OT' : otMode === 'all_reg' ? 'REG' : 'OT+'}
                          </span>
                        )}
                        {multi && (
                          <>
                            {(aftDay || otMode || travDay) && ' '}
                            <button type="button" disabled={!canEditTimes}
                              onClick={(e) => { e.stopPropagation(); setDaySplitGrid(emp, d.date, wo.work_order_number, multi); }}
                              data-testid={`ts-multi-${emp.employee_name.replace(/\s+/g, '-').toLowerCase()}-${d.date}`}
                              title={(multiHere
                                ? `${myOt}h of this ${multi.day_total}h day (${multi.jobs} jobs) is overtime on this job${multi.pinned ? ' — regular hours set by PM' : multi.explicit ? ' — OT job set by PM' : ' — clock-in order'}`
                                : `${multi.day_total}h day across ${multi.jobs} jobs — this job is all regular; the OT is on ${multi.ot_job}`)
                                + (canEditTimes ? '. Click to set how many regular hours THIS job gets (the rest of it is OT)' : '')}
                              className={`${multiHere ? 'text-amber-600' : 'text-slate-500 hover:text-amber-600'} disabled:cursor-default`}>
                              {multiHere ? `OT+${myOt}` : `${multi.day_total}h DAY`}
                            </button>
                          </>
                        )}
                      </span>
                    ) : null;
                    const hasHistory = canEditTimes && (meta?.history || []).length > 0;
                    const tipHandlers = hasHistory ? {
                      onMouseEnter: (e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        setHistTip({ x: r.left + r.width / 2, y: r.bottom, emp: emp.employee_name, label: d.label, items: [...meta.history].reverse() });
                      },
                      onMouseLeave: () => setHistTip(null),
                    } : {};
                    if (att && !canEditTimes) {
                      return (
                        <td key={d.date} className="px-1 py-1 text-center border border-slate-300">
                          <span data-testid={`ts-att-${d.date}`} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${ATT_BADGE[att] || ''}`}>{ATT_LABELS[att] || att}</span>
                        </td>
                      );
                    }
                    return (
                      <td key={d.date} {...tipHandlers}
                        className={`px-1 py-1 text-center border border-slate-300 ${hasHistory ? 'bg-purple-50' : ''}`}>
                        {att && (
                          <span className="block mb-0.5" title={att === 'no_work' || att === 'on_site' ? 'Type hours to replace this tag' : 'Type hours if they did work part of the day'}>
                            <span data-testid={`ts-att-${d.date}`} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${ATT_BADGE[att] || ''}`}>{ATT_LABELS[att] || att}</span>
                          </span>
                        )}
                        {canEditTimes && !emp.is_fab ? (
                        <span className="inline-flex items-center gap-0.5">
                        <input type="number" step="0.5" key={`${d.date}:${val}:${gridVersion}`} defaultValue={val} placeholder="—"
                          data-testid={`ts-cell-${emp.employee_name.replace(/\s+/g, '-').toLowerCase()}-${d.date}`}
                          className="w-12 text-center font-bold bg-transparent border-b border-transparent hover:border-blue-400 focus:border-blue-500 focus:bg-blue-50 outline-none text-xs text-slate-900 placeholder-slate-300"
                          onFocus={e => e.target.select()}
                          onBlur={async (e) => {
                            const newVal = parseFloat(e.target.value);
                            if (!val && isNaN(newVal)) return;
                            if (val && newVal === val) return;
                            const want = isNaN(newVal) || newVal <= 0 ? 0 : newVal;
                            try {
                              await axios.post(`${API}/update-timesheet-hours`, {
                                employee_name: emp.employee_name,
                                date: d.date,
                                hours: want,
                                work_order_number: wo.work_order_number,
                              });
                              const fresh = await generate();
                              const row = fresh?.work_orders?.find(w => w.work_order_number === wo.work_order_number)?.employees?.find(x => x.employee_name === emp.employee_name);
                              const got = row ? (row.hours_by_day?.[d.date] || 0) : 0;
                              if (Math.abs(got - want) > 0.01) {
                                toast.warning(`${emp.employee_name} ${d.label}: server has ${got || 0}h (you entered ${want}h) — the day may be split across jobs or rounded; check PM Review`);
                              } else {
                                toast.success(`Updated ${emp.employee_name} ${d.label}`);
                              }
                            } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); generate(); }
                          }}
                        />
                        {meta?.edited && (
                          <span className="text-purple-600 text-[10px] leading-none cursor-default"
                            title={`Edited by ${emp.employee_name}${meta.edit_reason ? ` — "${meta.edit_reason}"` : ''}${meta.needs_reapproval ? ' · needs re-approval' : ''}`}>✏️</span>
                        )}
                        </span>
                        ) : isOwnCell && val && !emp.is_fab ? (
                          <button
                            onClick={() => { setCellEdit({ rowKey, wo: wo.work_order_number, date: d.date, label: d.label, hours: String(val), woValue: '', reason: '', status: meta?.status }); setCellWoResults([]); }}
                            data-testid={`ts-cell-edit-${d.date}`}
                            title="Tap to fix your hours — the change goes to your PM for approval"
                            className="text-xs font-bold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-500">
                            {val}{meta?.edited && <span className="text-purple-600 ml-0.5">✏️</span>}
                          </button>
                        ) : (
                          <span className="text-xs font-bold text-slate-900" title={emp.is_fab && val ? fabTitle : undefined} data-testid={emp.is_fab ? `ts-fab-cell-${emp.employee_name.replace(/\s+/g, '-').toLowerCase()}-${d.date}` : undefined}>
                            {val || '—'}{meta?.edited && <span className="text-purple-600 ml-0.5" title={meta.edit_reason || 'Edited'}>✏️</span>}
                            {emp.is_fab && val && meta?.status === 'pending' && <span className="block text-[8px] leading-none font-bold text-purple-600 mt-0.5" title="Awaiting shop foreman approval">PENDING</span>}
                          </span>
                        )}
                        {dayMarkers}
                      </td>
                    );
                  })}
                  <td className="px-3 py-1 text-center text-slate-900 font-bold border border-slate-300 bg-yellow-50">{emp.total || ''}</td>
                </tr>
                {cellEdit && cellEdit.rowKey === rowKey && (
                  <tr>
                    <td colSpan={dayLabels.length + 2} className="px-3 py-2 bg-blue-50 border border-slate-300" data-testid="ts-cell-editor">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-700">Fix <b>{cellEdit.label}</b> on <b className="font-mono">{cellEdit.wo}</b>:</span>
                        <label className="text-[11px] text-slate-500">Hours</label>
                        <input type="number" step="0.25" min="0.25" max="24" autoFocus value={cellEdit.hours}
                          onChange={e => setCellEdit(prev => ({ ...prev, hours: e.target.value }))}
                          data-testid="ts-cell-hours-input"
                          className="w-20 px-2 py-1 border border-slate-300 rounded text-xs text-slate-900 outline-none focus:border-blue-500" />
                        <label className="text-[11px] text-slate-500">Move to WO</label>
                        <div className="relative">
                          <input value={cellEdit.woValue} onChange={e => searchCellWo(e.target.value)}
                            placeholder={cellEdit.wo} data-testid="ts-cell-wo-input"
                            className="px-2 py-1 border border-slate-300 rounded text-xs text-slate-900 w-28 outline-none focus:border-blue-500" />
                          {cellWoResults.length > 0 && (
                            <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-slate-300 rounded shadow-lg w-56 max-h-48 overflow-auto">
                              {cellWoResults.map(p => (
                                <button key={p.project_number} data-testid={`ts-cell-wo-opt-${p.project_number}`}
                                  onClick={() => { setCellEdit(prev => ({ ...prev, woValue: p.project_number })); setCellWoResults([]); }}
                                  className="block w-full text-left px-2 py-1 text-xs text-slate-700 hover:bg-blue-50">
                                  <span className="font-mono font-bold text-blue-600">{p.project_number}</span> {p.client_name || p.title || ''}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <input value={cellEdit.reason} maxLength={200}
                          onChange={e => setCellEdit(prev => ({ ...prev, reason: e.target.value }))}
                          placeholder="Why the change? (your PM sees this)" data-testid="ts-cell-reason-input"
                          className="flex-1 min-w-[180px] px-2 py-1 border border-slate-300 rounded text-xs text-slate-900 outline-none focus:border-blue-500" />
                        <button onClick={saveCellEdit} disabled={cellSaving || !(cellEdit.reason || '').trim() || !cellEdit.hours}
                          data-testid="ts-cell-save"
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-xs font-medium">
                          {cellSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button onClick={() => { setCellEdit(null); setCellWoResults([]); }}
                          className="px-3 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded text-xs">Cancel</button>
                        {cellEdit.status === 'approved' && (
                          <span className="w-full text-[11px] text-amber-700">This day was already approved — saving sends it back to your PM for re-approval.</span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                {travelEdit && travelEdit.key === rowKey && (
                  <tr>
                    <td colSpan={dayLabels.length + 2} className="px-3 py-2 bg-sky-50 border border-slate-300" data-testid="ts-day-travel-editor">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs text-slate-700 inline-flex items-center gap-1"><Plane className="w-3.5 h-3.5 text-sky-600" /> Travel hours for <b>{emp.employee_name}</b> — billed at the travel rate, never OT:</span>
                        {travelEdit.days.map(dd => (
                          <label key={dd.date} className="inline-flex items-center gap-1 text-xs text-slate-700 bg-white border border-sky-200 rounded px-2 py-1">
                            <span className="font-semibold">{dd.label}</span>
                            <span className="text-slate-500">{dd.hours}h →</span>
                            <input type="number" step="0.5" min="0" max={dd.hours} value={travelEdit.values[dd.date]} placeholder="0"
                              data-testid={`ts-day-travel-input-${dd.date}`}
                              onChange={e => setTravelEdit(prev => ({ ...prev, values: { ...prev.values, [dd.date]: e.target.value } }))}
                              className="w-14 px-1.5 py-0.5 border border-slate-300 rounded text-xs text-slate-900 outline-none focus:border-sky-500" />
                            <button onClick={() => setTravelEdit(prev => ({ ...prev, values: { ...prev.values, [dd.date]: String(dd.hours) } }))}
                              className="text-[10px] text-sky-700 hover:underline" title="Whole day is travel" data-testid={`ts-day-travel-all-${dd.date}`}>all</button>
                          </label>
                        ))}
                        <button onClick={saveTravelEdit} disabled={travelSaving} data-testid="ts-day-travel-save"
                          className="px-3 py-1 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white rounded text-xs font-medium">{travelSaving ? 'Saving…' : 'Save'}</button>
                        <button onClick={() => setTravelEdit(null)} className="px-3 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded text-xs">Cancel</button>
                        <span className="w-full text-[11px] text-slate-500">Set hours to 0 to clear. Remaining hours on the day keep their normal reg / OT split.</span>
                      </div>
                    </td>
                  </tr>
                )}
                {editingWo && (
                  <tr>
                    <td colSpan={dayLabels.length + 2} className="px-3 py-2 bg-amber-50 border border-slate-300">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-600">Move <b>{emp.employee_name}</b> from <b className="font-mono">{wo.work_order_number}</b> to:</span>
                        <div className="relative">
                          <input autoFocus value={woEdit.value} onChange={e => searchWoEdit(e.target.value)}
                            placeholder="26-XXXX" data-testid="ts-wo-input"
                            className="px-2 py-1 border border-slate-300 rounded text-xs text-slate-900 w-32 outline-none focus:border-blue-500" />
                          {woEditResults.length > 0 && (
                            <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-slate-300 rounded shadow-lg w-56 max-h-48 overflow-auto">
                              {woEditResults.map(p => (
                                <button key={p.project_number} data-testid={`ts-wo-opt-${p.project_number}`}
                                  onClick={() => { setWoEdit(prev => ({ ...prev, value: p.project_number })); setWoEditResults([]); }}
                                  className="block w-full text-left px-2 py-1 text-xs text-slate-700 hover:bg-blue-50">
                                  <span className="font-mono font-bold text-blue-600">{p.project_number}</span> {p.client_name || p.title || ''}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <button onClick={saveWoReassign} data-testid="ts-wo-save"
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium">Save</button>
                        <button onClick={() => { setWoEdit(null); setWoEditResults([]); }}
                          className="px-3 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded text-xs">Cancel</button>
                        {(woEdit.dayOptions || []).length > 0 && (
                          <div className="w-full flex items-center gap-1.5 flex-wrap pt-1" data-testid="ts-wo-days">
                            <span className="text-[11px] text-slate-500 font-medium">Days to move:</span>
                            {woEdit.dayOptions.map(o => {
                              const on = (woEdit.selectedDays || []).includes(o.date);
                              return (
                                <button key={o.date} type="button"
                                  data-testid={`ts-wo-day-${o.date}`}
                                  onClick={() => setWoEdit(prev => ({
                                    ...prev,
                                    selectedDays: on ? (prev.selectedDays || []).filter(d => d !== o.date) : [...(prev.selectedDays || []), o.date],
                                  }))}
                                  className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors ${on ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-300 text-slate-500 hover:border-blue-400'}`}>
                                  {o.label} · {o.hours}h
                                </button>
                              );
                            })}
                            <span className="text-[10px] text-slate-500">un-select days that should stay on {wo.work_order_number}</span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {canEditTimes && (
            <div className="px-3 py-1.5 border-t border-slate-200 bg-white" data-testid={`ts-add-person-bar-${wo.work_order_number}`}>
              <AddPersonRow wo={wo} dayLabels={dayLabels} onAdded={() => generate()} />
            </div>
          )}
          {(wo.consumables.length > 0 || (wo.purchases || []).length > 0 || (wo.equipment || []).length > 0 || (wo.rentals || []).length > 0 || canEditTimes) && (
            <div className="px-4 py-2 border-t border-slate-300 bg-slate-50">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-slate-500 font-semibold">Consumables, Purchases & Equipment:</p>
                {canEditTimes && (
                  <button
                    onClick={() => openItemsModal(wo.work_order_number)}
                    data-testid={`ts-add-items-${wo.work_order_number}`}
                    className="text-[11px] px-2 py-0.5 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
                  >
                    + Add Items
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1">
                {wo.consumables.map((c, i) => (
                  <span key={`c-${i}`} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-white text-slate-700 rounded border border-slate-300">
                    {c.item_name} x{c.quantity} <span className="text-slate-500">({c.source === 'tool_crib' ? `tool crib · ${c.kit_number || 'kit'}` : c.employee_name})</span>
                    {canEditTimes && c.id && (
                      <button onClick={() => deleteItem(c.id)} title="Remove item" data-testid={`ts-del-item-${c.id}`}
                        className="text-slate-500 hover:text-red-600 leading-none font-bold">×</button>
                    )}
                  </span>
                ))}
                {(wo.purchases || []).map((p, i) => (
                  <span key={`p-${i}`} title="Purchase" className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-amber-50 text-amber-800 rounded border border-amber-300">
                    {p.item_name} x{p.quantity}{Number(p.unit_cost) > 0 && <span className="text-amber-600">@ ${Number(p.unit_cost).toFixed(2)}</span>}{p.po_number && <span className="text-amber-600" data-testid={`ts-item-po-${p.id}`}>· PO {p.po_number}</span>}{p.source === 'tool_crib' && <span className="text-amber-600">· tool crib</span>}
                    {canEditTimes && p.id && (
                      <button onClick={() => deleteItem(p.id)} title="Remove item" data-testid={`ts-del-item-${p.id}`}
                        className="text-amber-600 hover:text-red-600 leading-none font-bold">×</button>
                    )}
                  </span>
                ))}
                {(wo.equipment || []).map((q, i) => (
                  <span key={`e-${i}`} title="365 equipment at the listed rate" className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-blue-50 text-blue-900 rounded border border-blue-300" data-testid={`ts-eq-chip-${q.id}`}>
                    <Truck className="w-3 h-3" /> {q.item_name} · {q.quantity} {q.rate_basis || 'day'}{Number(q.quantity) === 1 ? '' : 's'} <span className="text-blue-600">@ ${Number(q.unit_cost || 0).toFixed(2)}</span>
                    {canEditTimes && q.id && (
                      <button onClick={() => deleteItem(q.id)} title="Remove item" data-testid={`ts-del-item-${q.id}`}
                        className="text-blue-700 hover:text-red-600 leading-none font-bold">×</button>
                    )}
                  </span>
                ))}
                {(wo.rentals || []).map((q, i) => (
                  <span key={`r-${i}`} title={`Rented from ${q.vendor || 'Sunbelt'} — our cost $${Number(q.cost_rate || 0).toFixed(2)} + ${q.margin_pct ?? 30}% margin`} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-orange-50 text-orange-900 rounded border border-orange-300" data-testid={`ts-rent-chip-${q.id}`}>
                    <Building2 className="w-3 h-3" /> {q.item_name} · {q.quantity} {q.rate_basis === 'month' ? '× 4-wk' : q.rate_basis === 'trip' ? (Number(q.quantity) === 1 ? 'trip' : 'trips') : `${q.rate_basis || 'day'}${Number(q.quantity) === 1 ? '' : 's'}`} <span className="text-orange-700">@ ${Number(q.unit_cost || 0).toFixed(2)}</span>{q.po_number && <span className="font-mono text-[10px] bg-white/70 border border-orange-200 rounded px-1" data-testid={`ts-rent-po-${q.id}`}>PO {q.po_number}</span>}
                    {canEditTimes && q.id && (
                      <button onClick={() => deleteItem(q.id)} title="Remove item" data-testid={`ts-del-item-${q.id}`}
                        className="text-orange-700 hover:text-red-600 leading-none font-bold">×</button>
                    )}
                  </span>
                ))}
                {wo.consumables.length === 0 && (wo.purchases || []).length === 0 && (wo.equipment || []).length === 0 && (wo.rentals || []).length === 0 && (
                  <span className="text-xs text-slate-500">None yet</span>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {data && data.work_orders.length > 0 && (
        <div className="text-right text-sm text-slate-500">
          Grand Total: <span className="text-slate-900 font-bold">{data.grand_total_hours}h</span>
        </div>
      )}

      {/* Add consumables/purchases/equipment to a WO's timesheet — same tick-sheet as the toolbox packs */}
      {itemsWo && (() => {
        const liveWo = data?.work_orders?.find(w => w.work_order_number === itemsWo);
        const existing = [...(liveWo?.equipment || []).map(e => ({ ...e, kind: 'equipment' })), ...(liveWo?.rentals || []).map(e => ({ ...e, kind: 'rental' })), ...(liveWo?.consumables || []).map(c => ({ ...c, kind: c.kind || 'consumable' })), ...(liveWo?.purchases || []).map(p => ({ ...p, kind: 'purchase' }))];
        return (
          <FieldItemPicker workOrder={itemsWo} dayOptions={dayLabels} defaultDate={itemDate} existing={existing} onDeleteExisting={deleteItem}
            onClose={() => setItemsWo(null)} onAdded={() => generate()} />
        );
      })()}

      {/* Admin change log */}
      {auditOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setAuditOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-6" onClick={e => e.stopPropagation()} data-testid="ts-audit-modal">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><History className="w-5 h-5 text-blue-600" /> Timesheet Change Log</h2>
              <button onClick={() => setAuditOpen(false)}><X className="w-5 h-5 text-slate-500 hover:text-slate-700" /></button>
            </div>
            <div className="p-4 max-h-[70vh] overflow-y-auto">
              {auditLoading ? <div className="text-center text-slate-500 py-8">Loading…</div> :
                auditLogs.length === 0 ? <div className="text-center text-slate-500 py-8" data-testid="ts-audit-empty">No timesheet changes recorded yet.</div> : (
                  <table className="w-full text-xs">
                    <thead className="text-slate-500">
                      <tr className="text-left">
                        <th className="py-1 pr-2">When</th><th className="py-1 pr-2">Who</th><th className="py-1 pr-2">Action</th>
                        <th className="py-1 pr-2">Employee</th><th className="py-1 pr-2">WO</th><th className="py-1 pr-2">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((l, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="py-1.5 pr-2 whitespace-nowrap text-slate-600">{new Date(l.changed_at).toLocaleString()}</td>
                          <td className="py-1.5 pr-2 text-slate-800">{l.changed_by} <span className="text-slate-500">({(l.changed_by_role || '').replace(/_/g, ' ')})</span></td>
                          <td className="py-1.5 pr-2"><span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{(l.action || '').replace(/_/g, ' ')}</span></td>
                          <td className="py-1.5 pr-2 text-slate-800">{l.employee_name || '—'}</td>
                          <td className="py-1.5 pr-2 font-mono text-slate-700">{l.work_order_number || '—'}</td>
                          <td className="py-1.5 pr-2 text-slate-700">
                            {l.note ? l.note : (l.old_value !== null && l.old_value !== undefined ? `${l.old_value} → ${l.new_value ?? '—'}` : (l.new_value ?? ''))}
                            {l.date ? <span className="text-slate-500"> · {l.date}</span> : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Normalized 0..1 similarity (Levenshtein ratio) for client-side name matching.
const strSim = (a, b) => {
  a = (a || '').toLowerCase().trim();
  b = (b || '').toLowerCase().trim();
  if (!a || !b) return 0;
  if (a === b) return 1;
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return 1 - dp[m][n] / Math.max(m, n);
};

function ApprovalTab() {
  const [data, setData] = useState({ crew: [], contractors: [], not_clocked_in: [], by_work_order: [] });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(today());
  const [expanded, setExpanded] = useState(null);
  const [approvedEmps, setApprovedEmps] = useState(new Set());
  const [otModes, setOtModes] = useState({});   // employee_id -> mode set this session (before reload)
  const [otBusy, setOtBusy] = useState(null);
  const [viewMode, setViewMode] = useState('employees'); // 'employees' or 'jobs'
  const [editing, setEditing] = useState(null); // event_id being edited
  const [editForm, setEditForm] = useState({});
  const [mergePrompt, setMergePrompt] = useState(null); // 409 duplicate_day detail while converting a contractor
  const [editWOResults, setEditWOResults] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [addOvertime, setAddOvertime] = useState(false);
  const [addShift, setAddShift] = useState('auto'); // auto = let the times decide (12:00–22:30 → afternoons)
  const [addEmpId, setAddEmpId] = useState('');
  const [addContractorName, setAddContractorName] = useState('');
  const [addIsContractor, setAddIsContractor] = useState(false);
  const [addWO, setAddWO] = useState('');
  const [addWOResults, setAddWOResults] = useState([]);
  const [addSelectedWO, setAddSelectedWO] = useState(null);
  const [addStart, setAddStart] = useState('07:00');
  const [addEnd, setAddEnd] = useState('');
  const [addLunch, setAddLunch] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${SHOP_API}/field-crew-review`, { params: { date: selectedDate } });
      setData(r.data);
      setApprovedEmps(new Set());
      setOtModes({});
      if (r.data.auto_matched?.length) {
        toast.success(`Matched ${r.data.auto_matched.length} PSI name${r.data.auto_matched.length !== 1 ? 's' : ''} to employees: ${r.data.auto_matched.map(m => `${m.typed} → ${m.employee}`).join(', ')}`, { duration: 9000 });
      }
    } catch { toast.error('Failed to load field crew'); }
    setLoading(false);
  }, [selectedDate]);

  useEffect(() => { load(); }, [load]);

  // Load all employees for the add form
  useEffect(() => {
    axios.get(`${SHOP_API}/attendance/today`).then(r => {
      const all = [...(r.data.clocked_in || []), ...(r.data.not_clocked_in || [])];
      setAllEmployees(all);
    }).catch(() => {});
  }, []);

  const addEntry = async () => {
    try {
      const body = {
        date: selectedDate,
        clock_in_time: addStart,
        clock_out_time: addEnd || undefined,
        work_order_number: addSelectedWO?.project_number || addWO || '',
        took_lunch: addLunch,
        is_contractor: addIsContractor,
        overtime: addOvertime,
        ...(addShift !== 'auto' ? { shift: addShift } : {}),
      };
      if (addIsContractor) {
        if (!addContractorName.trim()) { toast.error('Enter contractor name'); return; }
        body.employee_name = addContractorName;
      } else {
        if (!addEmpId) { toast.error('Select employee'); return; }
        body.employee_id = addEmpId;
      }
      await axios.post(`${SHOP_API}/field-crew-add`, body);
      toast.success('Entry added');
      setShowAdd(false); setAddEmpId(''); setAddContractorName(''); setAddWO(''); setAddSelectedWO(null); setAddStart('07:00'); setAddEnd(''); setAddOvertime(false); setAddShift('auto');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const shiftDate = (days) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const dayOfWeek = new Date(selectedDate + 'T12:00:00').getDay();
  const weekDays = ['S','M','T','W','T','F','S'];

  // OT modes are per card = per job (clock event), so a 2× breakdown on job A leaves job B's hours alone.
  const cardKey = (c) => c.clock_event_id || c.employee_id;
  const approveEmployee = async (c, otMode = '8_ot') => {
    const empId = c.employee_id;
    try {
      const r = await axios.post(`${API}/crew-approve`, { employee_id: empId, date: selectedDate, work_order_number: c.work_order_number || undefined, ...otBody(otMode) });
      const n = r.data.approved || 0;
      toast.success(`Approved (${otLabel(otMode)}) — ${n} entr${n === 1 ? 'y' : 'ies'}`);
      setApprovedEmps(prev => new Set([...prev, empId]));
      setOtModes(prev => ({ ...prev, [cardKey(c)]: otMode }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Approve failed'); }
  };

  // Approved day → change the OT split of this job's hours only (approval + approver untouched)
  const changeOtMode = async (c, otMode) => {
    setOtBusy(cardKey(c));
    try {
      const r = await axios.post(`${API}/set-ot-mode`, { employee_id: c.employee_id, date: selectedDate, work_order_number: c.work_order_number || undefined, ...otBody(otMode) });
      toast.success(`${c.work_order_number ? `${c.work_order_number} ` : 'Day '}set to ${otLabel(otMode)}${r.data.approved ? ' — still approved' : ''}`);
      setOtModes(prev => ({ ...prev, [cardKey(c)]: otMode }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not change OT mode'); }
    setOtBusy(null);
  };

  // Day split across jobs: pick which job carries the overtime (hours past 8)
  const setOtJob = async (c, wo) => {
    setOtBusy(`otjob-${c.employee_id}`);
    try {
      await axios.post(`${API}/set-ot-job`, { employee_id: c.employee_id, date: selectedDate, work_order_number: wo });
      toast.success(wo ? `${c.employee_name}: the day's OT now lands on ${wo}` : `${c.employee_name}: OT back to clock-in order`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not move the OT'); }
    setOtBusy(null);
  };

  // Pin the regular hours of ONE job on a split day (rest of that job = OT); the other jobs keep their current regular
  // hours so e.g. 10h job → 8 reg + 2 OT and the 3h breakdown → 0 reg = all OT.
  const setDaySplit = async (c, wo, regHours, { topUpOthers = false } = {}) => {
    const pins = {};
    for (const j of c.day_jobs || []) pins[j.wo] = j.wo === wo ? regHours : j.reg;
    const total = Object.values(pins).reduce((a, b) => a + b, 0);
    if (total > 8) {
      // squeeze the others (last clocked-in first) so the day still has 8h regular
      let over = total - 8;
      for (const j of [...(c.day_jobs || [])].reverse()) {
        if (j.wo === wo || over <= 0) continue;
        const cut = Math.min(pins[j.wo], over); pins[j.wo] -= cut; over -= cut;
      }
    } else if (topUpOthers && total < 8) {
      // "all OT" on one job frees regular hours — hand them to the other jobs (clock-in order) up to what they worked
      let free = 8 - total;
      for (const j of c.day_jobs || []) {
        if (j.wo === wo || free <= 0) continue;
        const add = Math.min(Math.max(0, (j.hours || 0) - pins[j.wo]), free); pins[j.wo] += add; free -= add;
      }
    }
    setOtBusy(`otjob-${c.employee_id}`);
    try {
      await axios.post(`${API}/set-day-split`, { employee_id: c.employee_id, date: selectedDate, reg_by_wo: pins });
      toast.success(`${c.employee_name}: ${Object.entries(pins).map(([w, h]) => `${w} ${h}h reg`).join(' · ')} — the rest is OT`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not set the split'); }
    setOtBusy(null);
  };

  // Hours WORKED on one job of a split day (e.g. the 5h breakdown that got zeroed as a duplicate) — then the reg box decides how much is OT.
  const setJobHours = async (c, wo, hours, prev) => {
    if (hours <= 0 && !window.confirm(`Remove ${c.employee_name}'s ${prev}h on ${wo} for ${selectedDate}?`)) { await load(); return; }
    setOtBusy(`otjob-${c.employee_id}`);
    try {
      await axios.post(`${API}/update-timesheet-hours`, { employee_name: c.employee_name, date: selectedDate, hours, work_order_number: wo });
      toast.success(hours > 0 ? `${c.employee_name}: ${hours}h on ${wo} — now set its regular hours (0 = all OT)` : `${c.employee_name}: ${wo} removed for the day`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not set the hours'); }
    setOtBusy(null);
  };

  const clockOutEmployee = async (c) => {
    const empId = typeof c === 'string' ? c : c.employee_id;
    const inDay = c?.clock_in_time ? new Date(c.clock_in_time).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' }) : null;
    const stale = !!inDay && inDay !== today();
    let clockOutTime = null;
    if (stale) {
      const def = new Date(new Date(c.clock_in_time).getTime() + 8.5 * 3600 * 1000)
        .toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Toronto' });
      const v = window.prompt(`${c.employee_name || 'This employee'} clocked in on ${inDay} and never clocked out.\n\nEnter the time they finished THAT day (24h, e.g. 15:30):`, def);
      if (v === null) return;
      if (!/^\d{1,2}:\d{2}$/.test(v.trim())) { toast.error('Enter a time like 15:30'); return; }
      clockOutTime = v.trim();
    } else if (!window.confirm('Clock out this employee?')) return;
    try {
      // Offline phones send the exact moment pressed (ISO) so a late upload still lands at the right time
      const outTime = clockOutTime || new Date().toISOString();
      const sent = await sendOrQueue('crew_clock_out', `Clock out ${c.employee_name || 'crew'} at ${clockOutTime || fmtTime(outTime)}`,
        { employee_ids: [empId], clock_out_time: outTime });
      if (sent.queued) { toast.warning(`No signal — ${c.employee_name || 'the'} clock-out is saved on this phone and posts when back in service.`, { duration: 6000 }); return; }
      const done = (sent.data.results || []).filter(x => x.status === 'clocked_out' && x.employee_id);
      if (done.length === 0) { toast.error('No open clock-in found for this employee — refreshing'); load(); return; }
      toast.success(clockOutTime ? `Clocked out at ${clockOutTime} on ${inDay} — ${done[0].hours_worked}h` : 'Clocked out');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleExpand = (empId) => setExpanded(expanded === empId ? null : empId);

  // Convert a stored timestamp (UTC-aware or naive-ET) into an ET wall-clock
  // value for the datetime-local input (YYYY-MM-DDTHH:MM).
  const toETLocalInput = (iso) => {
    if (!iso) return '';
    try {
      const s = new Date(iso).toLocaleString('sv-SE', { timeZone: 'America/Toronto' });
      return s.replace(' ', 'T').slice(0, 16);
    } catch { return (iso || '').slice(0, 16); }
  };

  const startEdit = (c) => {
    setEditing(c.clock_event_id || c.employee_id);
    setEditWOResults([]);
    setEditForm({
      clock_in_time: toETLocalInput(c.clock_in_time),
      clock_out_time: toETLocalInput(c.clock_out_time),
      took_lunch: c.took_lunch ?? true,
      afternoon_shift: !!c.afternoon_shift,
      afternoon_touched: false,
      travel_hours: c.travel_hours ? String(c.travel_hours) : '',
      orig_travel: c.travel_hours || 0,
      double_time: c.ot_mode === 'all_dt' ? (c.dt_reason || 'stat_holiday') : '',
      orig_double_time: c.ot_mode === 'all_dt' ? (c.dt_reason || 'stat_holiday') : '',
      employee_id: c.employee_id,
      notes: c.notes || '',
      hours_override: '',
      work_order_number: c.work_order_number || '',
      orig_wo: c.work_order_number || '',
      is_contractor: !!c.is_contractor,
      convert_to_employee_id: '',
      update_psi: true,
    });
  };

  const saveEdit = async (eventId, extra = {}) => {
    try {
      const body = { ...extra };
      if (editForm.clock_in_time) body.clock_in_time = editForm.clock_in_time + ':00';
      if (editForm.clock_out_time) body.clock_out_time = editForm.clock_out_time + ':00';
      body.took_lunch = editForm.took_lunch;
      if (editForm.afternoon_touched) body.afternoon_shift = !!editForm.afternoon_shift;
      if ((parseFloat(editForm.travel_hours) || 0) !== (editForm.orig_travel || 0)) body.travel_hours = parseFloat(editForm.travel_hours) || 0;
      if ((editForm.double_time || '') !== (editForm.orig_double_time || '')) body.double_time = editForm.double_time || '';
      if (editForm.notes) body.notes = editForm.notes;
      if (editForm.hours_override) body.hours_override = parseFloat(editForm.hours_override);
      if (editForm.work_order_number !== editForm.orig_wo) {
        body.work_order_number = editForm.work_order_number;
        body.update_psi = editForm.update_psi !== false;
      }
      if (editForm.is_contractor && editForm.convert_to_employee_id) {
        body.convert_to_employee_id = editForm.convert_to_employee_id;
        body.remember_alias = editForm.remember_alias !== false;
      }
      const res = await axios.put(`${SHOP_API}/field-crew-edit/${eventId}`, body);
      if ('double_time' in body && editForm.employee_id) {
        setOtModes(prev => ({ ...prev, [eventId]: body.double_time ? `dt:${body.double_time}` : '8_ot' }));
      }
      if (body.convert_to_employee_id) {
        const d = res.data || {};
        const who = d.employee_name || 'employee';
        const kept = d.kept === 'existing' ? ` — kept ${who}'s existing hours, contractor row removed` : d.kept === 'contractor' ? ` — replaced ${who}'s existing hours with the crew clock-in (re-approve the day)` : '';
        const alias = d.remembered_alias ? ` · "${d.remembered_alias}" will match ${who} automatically from now on` : '';
        toast.success(`Converted to employee${kept}${alias}`, { duration: 7000 });
      } else {
        toast.success(body.double_time ? `Updated — ${DT_LABEL[body.double_time]}: every hour on ${editForm.work_order_number || 'this job'} at double time` : 'Updated');
      }
      setMergePrompt(null);
      setEditing(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d && d.code === 'duplicate_day') { setMergePrompt(d); return; }
      toast.error(typeof d === 'string' ? d : 'Update failed');
    }
  };

  // Best fuzzy employee match for a (likely mistyped) contractor name — used to
  // pre-select the right person when converting a contractor to an employee.
  const bestEmployeeMatch = (name) => {
    const parts = (name || '').toLowerCase().trim().split(/\s+/);
    if (parts.length < 2) return '';
    const firstIn = parts[0], lastIn = parts[parts.length - 1];
    let bestId = '', bestScore = 0;
    for (const e of allEmployees) {
      const ep = (e.employee_name || e.full_name || '').toLowerCase().trim().split(/\s+/);
      if (ep.length < 2) continue;
      const firstDb = ep[0], lastDb = ep[ep.length - 1];
      const lastScore = strSim(lastDb, lastIn);
      const firstOk = firstDb[0] === firstIn[0] || firstDb.startsWith(firstIn) || firstIn.startsWith(firstDb) || strSim(firstDb, firstIn) >= 0.7;
      if (lastScore >= 0.78 && firstOk && lastScore > bestScore) { bestId = e.employee_id || e.id; bestScore = lastScore; }
    }
    return bestId;
  };

  const startConvert = (c) => {
    const cardId = c.clock_event_id || c.employee_id;
    setExpanded(cardId);
    setEditing(cardId);
    setEditWOResults([]);
    setEditForm({
      clock_in_time: toETLocalInput(c.clock_in_time),
      clock_out_time: toETLocalInput(c.clock_out_time),
      took_lunch: c.took_lunch ?? true,
      afternoon_shift: !!c.afternoon_shift,
      afternoon_touched: false,
      travel_hours: c.travel_hours ? String(c.travel_hours) : '',
      orig_travel: c.travel_hours || 0,
      double_time: c.ot_mode === 'all_dt' ? (c.dt_reason || 'stat_holiday') : '',
      orig_double_time: c.ot_mode === 'all_dt' ? (c.dt_reason || 'stat_holiday') : '',
      employee_id: c.employee_id,
      notes: c.notes || '',
      hours_override: '',
      work_order_number: c.work_order_number || '',
      orig_wo: c.work_order_number || '',
      is_contractor: true,
      convert_to_employee_id: bestEmployeeMatch(c.employee_name),
      remember_alias: true,
      typed_name: (c.employee_name || '').replace('(Contractor)', '').trim(),
      update_psi: true,
    });
    setMergePrompt(null);
  };

  const deleteEvent = async (eventId, name) => {
    if (!window.confirm(`Delete entry for ${name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${SHOP_API}/field-crew-event/${eventId}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  // Wrong tracker: send a field entry back to Shop Tracker (same clock in/out → shop clock event + job session)
  const moveToShop = async (c, force = false) => {
    const wo = c.work_order_number || '';
    if (!force && !window.confirm(`Move ${c.employee_name}'s field entry to Shop Tracker?\n\nTheir ${fmtTime(c.clock_in_time)}${c.clock_out_time ? ` → ${fmtTime(c.clock_out_time)}` : ' (still clocked in)'} becomes a shop clock-in with a job session on ${wo ? `WO ${wo}` : 'the same work order'}; the field entry is removed.${c.is_approved ? '\n\n⚠️ This field day is already APPROVED — it will be replaced.' : ''}`)) return;
    try {
      const r = await axios.post(`${SHOP_API}/move-to-shop`, { event_id: c.clock_event_id, force: force || c.is_approved });
      toast.success(r.data.still_clocked_in
        ? `${c.employee_name} moved to Shop Tracker — still clocked in, so they clock out in Shop Tracker`
        : `${c.employee_name} moved to Shop Tracker — ${r.data.hours}h on WO ${wo}, pending foreman approval`, { duration: 8000 });
      load();
    } catch (e) {
      if (e.response?.status === 409) { if (window.confirm(`${e.response.data.detail}\n\nMove it anyway?`)) return moveToShop(c, true); }
      else toast.error(e.response?.data?.detail || 'Failed to move to Shop Tracker');
    }
  };

  // Wrong tracker the other way: pull a guy clocked into Shop Tracker onto a field job
  const [pullFor, setPullFor] = useState(null);
  const [pullWO, setPullWO] = useState('');
  const [pullWOResults, setPullWOResults] = useState([]);
  const [pullSelected, setPullSelected] = useState(null);
  const [pulling, setPulling] = useState(false);
  const searchPullWO = async (q) => {
    setPullWO(q); setPullSelected(null);
    if (q.length < 2) { setPullWOResults([]); return; }
    try { const r = await axios.get(`${SHOP_API}/work-orders`, { params: { q } }); setPullWOResults(r.data.slice(0, 6)); }
    catch { setPullWOResults([]); }
  };
  const pullToField = async (s, force = false) => {
    const wo = pullSelected?.project_number || pullWO.trim();
    if (!wo) { toast.error('Pick the field work order first'); return; }
    if (!force && !window.confirm(`Move ${s.employee_name}'s Shop Tracker day to Field Tracker on WO ${wo}?\n\nTheir shop clock-in, job sessions and shop time entries for ${selectedDate} are removed and become a field entry.`)) return;
    setPulling(true);
    try {
      const r = await axios.post(`${SHOP_API}/move-to-field`, { employee_id: s.employee_id, date: selectedDate, work_order_number: wo, force });
      toast.success(r.data.still_clocked_in
        ? `${s.employee_name} is now clocked in on Field Tracker (WO ${wo}) — they clock out in Field Tracker`
        : `${s.employee_name} moved to Field Tracker — ${r.data.hours}h on WO ${wo}`, { duration: 8000 });
      setPullFor(null); setPullWO(''); setPullSelected(null); setPullWOResults([]);
      load();
    } catch (e) {
      if (e.response?.status === 409) { if (window.confirm(`${e.response.data.detail}\n\nMove it anyway?`)) { setPulling(false); return pullToField(s, true); } }
      else toast.error(e.response?.data?.detail || 'Failed to move to Field Tracker');
    } finally { setPulling(false); }
  };

  const activeCount = data.crew.filter(c => !c.clock_out_time && c.active_job).length;
  const totalCrew = data.crew.length;

  return (
    <div className="space-y-4">
      {/* Date Navigation */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2">
          <button onClick={() => shiftDate(-1)} className="text-slate-500 hover:text-slate-900"><ChevronLeft className="w-4 h-4" /></button>
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
            data-testid="field-crew-date"
            className="bg-transparent text-slate-900 text-sm border-none outline-none" />
          <button onClick={() => shiftDate(1)} className="text-slate-500 hover:text-slate-900"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-0.5">
          {weekDays.map((d, i) => (
            <button key={i} onClick={() => {
              const diff = i - dayOfWeek;
              shiftDate(diff);
            }} className={`w-7 h-7 rounded text-xs font-bold ${i === dayOfWeek ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* Stats + View Toggle */}
      <div className="flex gap-2 flex-wrap text-xs items-center">
        <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg">{totalCrew} clocked in</span>
        <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg">{activeCount} on jobs</span>
        <span className="px-2.5 py-1 bg-slate-200 text-slate-500 rounded-lg">{data.not_clocked_in?.length || 0} not in</span>
        {data.contractors?.length > 0 && (
          <span className="px-2.5 py-1 bg-amber-50 text-amber-700 rounded-lg">{data.contractors.length} contractors</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <div className="flex bg-white rounded-lg border border-slate-200 p-0.5">
            <button onClick={() => setViewMode('employees')}
              data-testid="approve-view-employees"
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'employees' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-white'}`}>
              By Employee
            </button>
            <button onClick={() => setViewMode('jobs')}
              data-testid="approve-view-jobs"
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'jobs' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-white'}`}>
              By Job
            </button>
          </div>
          <button onClick={load} className="text-slate-500 hover:text-slate-900 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <button onClick={() => setShowAdd(!showAdd)} className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-medium">
            <Plus className="w-3 h-3 inline mr-1" />Add Entry
          </button>
        </div>
      </div>

      {/* Add Entry Form */}
      {showAdd && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3">
          <h4 className="text-sm font-semibold text-slate-900">Add Field Entry</h4>
          <div className="flex gap-2 mb-2">
            <button onClick={() => setAddIsContractor(false)}
              className={`px-3 py-1 rounded text-xs font-medium ${!addIsContractor ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>Employee</button>
            <button onClick={() => setAddIsContractor(true)}
              className={`px-3 py-1 rounded text-xs font-medium ${addIsContractor ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-500'}`}>Contractor</button>
          </div>
          <div className="flex flex-wrap gap-2 items-end">
            {!addIsContractor ? (
              <div className="flex-1 min-w-[160px]">
                <label className="text-[10px] text-slate-500">Employee</label>
                <select value={addEmpId} onChange={e => setAddEmpId(e.target.value)}
                  className="w-full px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm">
                  <option value="">Select...</option>
                  {allEmployees.sort((a,b) => (a.employee_name||a.full_name||'').localeCompare(b.employee_name||b.full_name||'')).map(e => (
                    <option key={e.employee_id||e.id} value={e.employee_id||e.id}>{e.employee_name||e.full_name} ({e.shop_number||''})</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="flex-1 min-w-[160px]">
                <label className="text-[10px] text-slate-500">Contractor Name</label>
                <input type="text" value={addContractorName} onChange={e => setAddContractorName(e.target.value)}
                  placeholder="First Last" className="w-full px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm" />
              </div>
            )}
            <div className="flex-1 min-w-[140px] relative">
              <label className="text-[10px] text-slate-500">Work Order</label>
              <input type="text" value={addWO} onChange={e => {
                setAddWO(e.target.value); setAddSelectedWO(null);
                if (e.target.value.length >= 2) axios.get(`${SHOP_API}/work-orders`, { params: { q: e.target.value } }).then(r => setAddWOResults(r.data.slice(0, 5))).catch(() => {});
                else setAddWOResults([]);
              }} placeholder="Search WO#..." className="w-full px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm" />
              {addWOResults.length > 0 && !addSelectedWO && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-300 rounded max-h-32 overflow-y-auto">
                  {addWOResults.map(wo => (
                    <button key={wo.project_number} onMouseDown={e => { e.preventDefault(); setAddSelectedWO(wo); setAddWO(wo.project_number); setAddWOResults([]); }}
                      className="w-full text-left px-2 py-1.5 hover:bg-slate-100 text-xs">
                      <span className="font-mono text-blue-700">{wo.project_number}</span> <span className="text-slate-700">{wo.client_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Start</label>
              <input type="time" value={addStart} onChange={e => setAddStart(e.target.value)}
                className="block px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">End</label>
              <input type="time" value={addEnd} onChange={e => setAddEnd(e.target.value)}
                className="block px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm" />
            </div>
            <label className="flex items-center gap-1 text-xs text-slate-500">
              <input type="checkbox" checked={addLunch} onChange={e => setAddLunch(e.target.checked)} /> Lunch
            </label>
            <label className="flex items-center gap-1 text-xs text-amber-700" title="Call-ins etc. — every hour of this entry bills as overtime on the timesheet">
              <input type="checkbox" checked={addOvertime} onChange={e => setAddOvertime(e.target.checked)} data-testid="field-add-overtime-checkbox" /> All hours OT
            </label>
            <select value={addShift} onChange={e => setAddShift(e.target.value)} data-testid="field-add-shift-select"
              title="Auto picks the shift from the times: starts 11:00+ and runs past 6:30pm = afternoons ($2/hr), starts 7pm+ = nights"
              className="px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-xs text-slate-900">
              <option value="auto">Shift: auto from times</option>
              <option value="days">Days</option>
              <option value="afternoons">Afternoons ($2/hr)</option>
              <option value="nights">Nights</option>
            </select>
            <button onClick={addEntry} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium">Add</button>
            <button onClick={() => setShowAdd(false)} className="text-xs text-slate-500 hover:text-slate-900">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="text-center py-12 text-slate-500">Loading...</div> : (
        <div className="space-y-2">

          {viewMode === 'jobs' ? (
            /* ── By Work Order View ── */
            <>
              {(data.by_work_order || []).length === 0 && (
                <div className="text-center py-12 text-slate-500">No jobs for this date</div>
              )}
              {(data.by_work_order || []).map(wo => (
                <div key={wo.work_order} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-3 bg-slate-100 border-b border-slate-200 flex items-center justify-between cursor-pointer"
                    onClick={() => setExpanded(expanded === wo.work_order ? null : wo.work_order)}>
                    <div>
                      <span className="font-mono text-blue-700 font-bold">{wo.work_order}</span>
                      {wo.customer && <span className="text-slate-500 text-sm ml-2">— {wo.customer}</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500">{wo.employees.length} worker{wo.employees.length !== 1 ? 's' : ''}</span>
                      <span className="text-sm text-slate-900 font-bold">{wo.total_hours.toFixed(1)}h</span>
                      <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${expanded === wo.work_order ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                  {expanded === wo.work_order && (
                    <div className="divide-y divide-slate-700/30">
                      {wo.employees.map((emp, i) => {
                        const empApproved = approvedEmps.has(emp.employee_id) || emp.is_approved;
                        return (
                        <div key={i} className="flex items-center justify-between px-4 py-2.5 flex-wrap gap-y-1.5">
                          <div className="flex items-center gap-2">
                            {emp.is_contractor ? (
                              <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Contractor</span>
                            ) : (
                              <div className={`w-2 h-2 rounded-full ${!emp.clock_out_time ? 'bg-emerald-400' : 'bg-slate-400'}`} />
                            )}
                            <span className="text-sm text-slate-900 font-medium">{emp.employee_name}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-slate-500">{fmtTime(emp.clock_in_time)} — {emp.clock_out_time ? fmtTime(emp.clock_out_time) : 'on site'}</span>
                            <span className="text-sm text-slate-900 font-bold">{(emp.total_hours_today || 0).toFixed(1)}h</span>
                            {!emp.clock_out_time && !emp.is_contractor && (
                              <button onClick={(e) => { e.stopPropagation(); clockOutEmployee(emp); }}
                                data-testid={`byjob-clockout-${emp.employee_id}-${i}`}
                                className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-[10px] font-medium">
                                Clock Out
                              </button>
                            )}
                            {!empApproved && !emp.is_contractor ? (
                              <>
                                <button onClick={(e) => { e.stopPropagation(); approveEmployee(emp, '8_ot'); }}
                                  title="Approve — first 8h regular, rest overtime"
                                  data-testid={`byjob-approve-8ot-${emp.employee_id}-${i}`}
                                  className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-medium flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" /> 8 + OT
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); approveEmployee(emp, 'all_reg'); }}
                                  title="Approve — every hour regular (no overtime)"
                                  data-testid={`byjob-approve-allreg-${emp.employee_id}-${i}`}
                                  className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-medium">
                                  All Reg
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); approveEmployee(emp, 'all_ot'); }}
                                  title="Approve — every hour overtime (weekend shifts)"
                                  data-testid={`byjob-approve-allot-${emp.employee_id}-${i}`}
                                  className="px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-[10px] font-medium">
                                  All OT
                                </button>
                              </>
                            ) : empApproved ? (
                              <>
                                <span className="px-2 py-1 text-emerald-700 text-[10px] font-medium flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" /> Approved
                                </span>
                                <OtModeToggle current={otModes[cardKey(emp)] || otKey(emp.ot_mode, emp.dt_reason)} busy={otBusy === cardKey(emp)}
                                  onPick={(m) => changeOtMode(emp, m)} testPrefix={`byjob-${emp.employee_id}-${i}`} />
                              </>
                            ) : null}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </>
          ) : (
            /* ── By Employee View ── */
            <>
          {/* Employee Cards */}
          {data.crew.map(c => {
            const isActive = !c.clock_out_time && c.active_job;
            const isDone = !!c.clock_out_time;
            const isApproved = approvedEmps.has(c.employee_id) || c.is_approved;
            const cardId = c.clock_event_id || c.employee_id;
            const isExpanded = expanded === cardId;

            return (
              <div key={cardId} className={`rounded-xl border overflow-hidden transition-colors ${
                isApproved ? 'bg-white/90 border-emerald-400' : isActive ? 'bg-white/85 border-emerald-300' : isDone ? 'bg-white/90 border-slate-300' : 'bg-white/85 border-amber-300'
              }`}>
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/60"
                  onClick={() => setExpanded(isExpanded ? null : cardId)}>
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                    isActive ? 'bg-emerald-400 animate-pulse' : isDone ? 'bg-slate-400' : 'bg-amber-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-900 font-semibold text-sm">{c.employee_name}</span>
                      {isDone && <span className="text-[10px] text-slate-700 bg-slate-200 px-1.5 py-0.5 rounded">Clocked out</span>}
                      {c.source === 'psi_crew_clockin' && <span className="text-[10px] text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">PSI</span>}
                      {c.zeroed_by_pm_edit && <span className="text-[10px] text-slate-600 bg-slate-200 px-1.5 py-0.5 rounded" data-testid={`dup-badge-${c.clock_event_id}`} title="Duplicate clock-in — zeroed when the PM set the day's total on the timesheet">Duplicate · 0h</span>}
                      {c.is_contractor && <span className="text-[10px] text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">Contractor</span>}
                    </div>
                    {c.active_job ? (
                      <p className="text-xs text-slate-600 mt-0.5">
                        Working on: <span className="font-mono text-blue-500">{c.active_job.work_order_number}</span> — {c.active_job.work_order_customer}
                      </p>
                    ) : isDone ? (
                      <p className="text-xs text-slate-500 mt-0.5">Done for the day</p>
                    ) : (
                      <p className="text-xs text-amber-600 mt-0.5">Clocked in — no active job</p>
                    )}
                    {c.wo_summary?.length > 0 && (
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                        {c.wo_summary.map((wo, i) => (
                          <span key={i} className="text-[10px] text-slate-700">
                            <span className="font-mono text-blue-600">{wo.wo}</span>
                            <span className="font-semibold ml-1">{wo.hours}h</span>
                          </span>
                        ))}
                      </div>
                    )}
                    {!c.is_contractor && c.day_jobs?.length > 1 && c.day_jobs.some(j => j.ot > 0 || j.pinned_reg != null) && (
                      <div className="mt-1 rounded-lg border border-amber-200 bg-amber-50/60 px-2 py-1.5" data-testid={`ot-job-picker-${c.employee_id}`} onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="text-[10px] text-slate-700 font-semibold" title="Per job: the hours worked, then how many of them are regular. One 8h regular day is shared across the jobs — the rest of each job is overtime.">
                            {c.day_total_hours}h day · {c.day_jobs.length} jobs · hours worked → regular per job (8 reg max{c.day_split_pinned ? ', set by PM' : c.ot_job_explicit ? `, OT on ${c.ot_job}` : ', clock-in order'}):
                          </span>
                          {c.day_jobs.map(j => (
                            <span key={j.wo} className="inline-flex items-center gap-1 text-[10px] bg-white border border-slate-300 rounded px-1.5 py-0.5" data-testid={`day-split-${c.employee_id}-${j.wo}`}>
                              <span className="font-mono text-blue-700 font-semibold">{j.wo}</span>
                              <input type="number" min="0" step="0.5" defaultValue={j.hours} key={`${j.wo}-h-${j.hours}`}
                                disabled={otBusy === `otjob-${c.employee_id}`}
                                onBlur={(e) => { const v = parseFloat(e.target.value); if (!Number.isNaN(v) && v !== j.hours) setJobHours(c, j.wo, v, j.hours); else e.target.value = j.hours; }}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                className={`w-11 h-5 px-1 border rounded text-[10px] text-center font-semibold ${j.hours > 0 ? 'border-slate-300 bg-white' : 'border-amber-400 bg-amber-50'}`}
                                title={j.hours > 0 ? `${j.wo}: ${j.hours}h worked — type to change the hours on this job` : `${j.wo}: no hours yet — type the hours worked on this job`}
                                data-testid={`day-split-hours-${c.employee_id}-${j.wo}`} />
                              <span className="text-slate-600">h</span>
                              <span className="text-slate-400">→</span>
                              <input type="number" min="0" max="8" step="0.5" defaultValue={j.reg} key={`${j.wo}-${j.reg}`}
                                disabled={otBusy === `otjob-${c.employee_id}` || !(j.hours > 0)}
                                onBlur={(e) => {
                                  const v = parseFloat(e.target.value);
                                  if (Number.isNaN(v) || v === j.reg) { e.target.value = j.reg; return; }
                                  if (v > j.hours + 0.001) { toast.error(`${j.wo} only has ${j.hours}h worked — change the hours box first, then the regular hours`); e.target.value = j.reg; return; }
                                  setDaySplit(c, j.wo, v);
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                className="w-11 h-5 px-1 border border-slate-300 rounded text-[10px] text-center font-semibold bg-white disabled:bg-slate-100 disabled:text-slate-400"
                                title={`${j.wo}: ${j.hours}h worked — how many are REGULAR (the rest is OT). 0 = all OT`} data-testid={`day-split-reg-${c.employee_id}-${j.wo}`} />
                              <span className="text-slate-600">reg</span>
                              {j.ot > 0 && <span className="text-amber-700 font-bold">+{j.ot} OT</span>}
                              {j.dt > 0 && <span className="text-rose-700 font-bold">+{j.dt} 2×</span>}
                              {j.reg > 0 && (
                                <button type="button" disabled={otBusy === `otjob-${c.employee_id}`} onClick={() => setDaySplit(c, j.wo, 0, { topUpOthers: true })}
                                  className="ml-0.5 text-[9px] text-amber-800 underline decoration-dotted hover:text-amber-950" title={`Every hour on ${j.wo} overtime — its regular hours move to the other job(s)`}
                                  data-testid={`day-split-allot-${c.employee_id}-${j.wo}`}>all OT</button>
                              )}
                            </span>
                          ))}
                          {(c.day_split_pinned || c.ot_job_explicit) && (
                            <button type="button" onClick={() => setOtJob(c, null)} data-testid={`ot-job-reset-${c.employee_id}`}
                              className="text-[10px] text-slate-500 underline decoration-dotted hover:text-slate-800" title="Back to automatic (regular hours fill the jobs in clock-in order, the last job carries the OT)">auto</button>
                          )}
                        </div>
                      </div>
                    )}
                    {c.employee_edited && (
                      <p className="text-[11px] text-purple-700 bg-purple-50 border border-purple-200 rounded px-2 py-0.5 mt-1 inline-block" data-testid={`employee-edited-flag-${c.employee_id}`}>
                        ✏️ Edited by employee{c.employee_edit?.original_hours != null ? ` — ${c.employee_edit.original_hours}h → ${c.employee_edit.hours}h` : ''}
                        {c.employee_edit?.edit_reason ? ` · "${c.employee_edit.edit_reason}"` : ''}
                        {c.employee_edit?.status === 'pending' ? ' · needs re-approval' : ''}
                      </p>
                    )}
                    <LocationBadge c={c} className="text-[10px] mt-1" />
                    <AckBadge c={c} />
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-900">{c.total_hours_today.toFixed(1)}h</span>
                      {c.lunch_deducted && <span className="text-[9px] text-amber-700" title="Lunch deducted">-L</span>}
                      {(() => {
                        const mode = otModes[cardKey(c)] || otKey(c.ot_mode, c.dt_reason);
                        if (mode?.startsWith('dt:') && c.total_hours_today > 0) return (
                          <span className="text-[10px] text-rose-700 bg-rose-100 border border-rose-300 px-2 py-0.5 rounded font-bold" title="Every hour charged and paid at double time" data-testid={`dt-pill-${c.employee_id}`}>
                            2× DT · {(DT_LABEL[mode.slice(3)] || 'Double time').toUpperCase()}
                          </span>
                        );
                        if (mode === 'all_reg') return null;
                        if (mode === 'all_ot' && c.total_hours_today > 0) return (
                          <span className="text-[10px] text-amber-700 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded font-bold" data-testid={`ot-pill-${c.employee_id}`}>
                            all {c.total_hours_today.toFixed(1)} OT
                          </span>
                        );
                        // Day split across jobs: this job's OT share (the 8h regular budget is per day, not per job)
                        const otHrs = c.job_split && c.day_jobs?.length > 1 ? c.job_split.ot : Math.max(0, c.total_hours_today - 8);
                        return otHrs > 0 ? (
                          <span className="text-[10px] text-amber-700 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded font-bold" data-testid={`ot-pill-${c.employee_id}`}
                            title={c.day_jobs?.length > 1 ? `${otHrs}h of this ${c.day_total_hours}h day's overtime is on this job` : undefined}>
                            +{otHrs.toFixed(1)} OT
                          </span>
                        ) : null;
                      })()}
                      {c.afternoon_shift && (
                        <span className="text-[10px] text-orange-700 bg-orange-100 border border-orange-300 px-2 py-0.5 rounded font-bold" title="Afternoon shift — $2/hr premium" data-testid={`aft-pill-${c.employee_id}`}>AFT</span>
                      )}
                      {c.night_shift && (
                        <span className="text-[10px] text-indigo-700 bg-indigo-100 border border-indigo-300 px-2 py-0.5 rounded font-bold" title="Night shift" data-testid={`night-pill-${c.employee_id}`}>NIGHT</span>
                      )}
                      {c.travel_hours > 0 && (
                        <span className="text-[10px] text-sky-700 bg-sky-100 border border-sky-300 px-2 py-0.5 rounded font-bold" title="Travel hours — travel rate, no OT" data-testid={`trv-pill-${c.employee_id}`}>{c.travel_hours}h TRV</span>
                      )}
                      <span className="text-xs text-slate-600">{c.job_count} jobs</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {c.is_contractor && (
                        <button onClick={(e) => { e.stopPropagation(); startConvert(c); }}
                          data-testid="make-employee-btn"
                          title="This person is actually a 365 employee — convert this entry"
                          className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-medium flex items-center gap-1">
                          <UserCheck className="w-3 h-3" /> Make Employee
                        </button>
                      )}
                      {!isDone && (
                        <button onClick={(e) => { e.stopPropagation(); clockOutEmployee(c); }}
                          data-testid={`pm-clockout-${c.employee_id}`}
                          className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-[10px] font-medium">
                          Clock Out
                        </button>
                      )}
                      {!isApproved && !c.is_contractor ? (
                        <>
                          <button onClick={(e) => { e.stopPropagation(); approveEmployee(c, '8_ot'); }}
                            title="Approve — first 8h regular, rest overtime"
                            data-testid={`field-approve-8ot-${c.employee_id}`}
                            className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-medium flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> 8 + OT
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); approveEmployee(c, 'all_reg'); }}
                            title="Approve — every hour regular (no overtime)"
                            data-testid={`field-approve-allreg-${c.employee_id}`}
                            className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-medium">
                            All Reg
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); approveEmployee(c, 'all_ot'); }}
                            title="Approve — every hour overtime (weekend shifts)"
                            data-testid={`field-approve-allot-${c.employee_id}`}
                            className="px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-[10px] font-medium">
                            All OT
                          </button>
                        </>
                      ) : isApproved ? (
                        <>
                          <span className="px-2 py-1 text-emerald-600 text-[10px] font-medium flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Approved
                          </span>
                          <OtModeToggle current={otModes[cardKey(c)] || otKey(c.ot_mode, c.dt_reason)} busy={otBusy === cardKey(c)}
                            onPick={(m) => changeOtMode(c, m)} testPrefix={`field-${c.employee_id}`} />
                        </>
                      ) : null}
                      <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-4 pb-3 border-t border-slate-200 pt-2 space-y-3">
                    {editing === cardId ? (
                      /* Edit Mode */
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-500 uppercase">Clock In</label>
                            <input type="datetime-local" value={editForm.clock_in_time}
                              onChange={(e) => setEditForm(f => ({...f, clock_in_time: e.target.value}))}
                              className="w-full p-1.5 bg-slate-50 border border-slate-300 rounded text-xs" />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500 uppercase">Clock Out</label>
                            <input type="datetime-local" value={editForm.clock_out_time}
                              onChange={(e) => setEditForm(f => ({...f, clock_out_time: e.target.value}))}
                              className="w-full p-1.5 bg-slate-50 border border-slate-300 rounded text-xs" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-500 uppercase">Hours Override</label>
                            <input type="number" step="0.5" placeholder="Auto-calc" value={editForm.hours_override}
                              onChange={(e) => setEditForm(f => ({...f, hours_override: e.target.value}))}
                              className="w-full p-1.5 bg-slate-50 border border-slate-300 rounded text-xs" />
                          </div>
                          <div className="flex flex-wrap items-end gap-x-3 gap-y-1.5 pb-1">
                            <label className="flex items-center gap-1 text-xs text-slate-600">
                              <input type="checkbox" checked={editForm.took_lunch}
                                onChange={(e) => setEditForm(f => ({...f, took_lunch: e.target.checked}))} />
                              Took Lunch
                            </label>
                            <label className="flex items-center gap-1 text-xs text-orange-700" title="$2/hr premium on every hour of this day — the whole day, every job. Set automatically from the times (12:00–22:30 = afternoons) — tick/untick to override">
                              <input type="checkbox" checked={!!editForm.afternoon_shift}
                                onChange={(e) => setEditForm(f => ({...f, afternoon_shift: e.target.checked, afternoon_touched: true}))} data-testid="edit-afternoon-checkbox" />
                              Afternoon shift{c.day_jobs?.length > 1 && <span className="text-[10px] text-orange-500" data-testid="edit-afternoon-day-hint">(whole day · {c.day_jobs.length} jobs)</span>}
                            </label>
                            {!editForm.is_contractor && (
                            <label className="flex items-center gap-1 text-xs text-sky-700" title="Away jobs: hours of this day that were the drive there / back — billed at the travel rate, never OT">
                              <Plane className="w-3 h-3" /> Travel
                              <input type="number" step="0.5" min="0" max="24" value={editForm.travel_hours} placeholder="0"
                                onChange={(e) => setEditForm(f => ({...f, travel_hours: e.target.value}))} data-testid="edit-travel-hours"
                                className="w-14 p-1 bg-slate-50 border border-slate-300 rounded text-xs text-slate-900" /> h
                            </label>
                            )}
                            {!editForm.is_contractor && (
                            <label className={`flex items-center gap-1 text-xs ${editForm.double_time ? 'text-rose-700 font-semibold' : 'text-slate-600'}`} title="Stat holiday or breakdown call: every worked hour on THIS job is charged to the customer and paid at double time (2×). Hours the same day on another job are not affected.">
                              2× DT
                              <select value={editForm.double_time} onChange={(e) => setEditForm(f => ({...f, double_time: e.target.value}))} data-testid="edit-double-time"
                                className={`p-1 rounded text-xs bg-slate-50 border ${editForm.double_time ? 'border-rose-400 text-rose-800' : 'border-slate-300 text-slate-900'}`}>
                                <option value="">No</option>
                                <option value="stat_holiday">Stat holiday</option>
                                <option value="breakdown">Breakdown</option>
                              </select>
                            </label>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-500 uppercase">Notes</label>
                          <input type="text" value={editForm.notes} placeholder="PM notes..."
                            onChange={(e) => setEditForm(f => ({...f, notes: e.target.value}))}
                            className="w-full p-1.5 bg-slate-50 border border-slate-300 rounded text-xs" />
                        </div>
                        {/* Work Order — searchable correction */}
                        <div className="relative">
                          <label className="text-[10px] text-slate-500 uppercase">Work Order #</label>
                          <input type="text" data-testid="edit-wo-input" value={editForm.work_order_number || ''}
                            placeholder="Search WO#..."
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditForm(f => ({...f, work_order_number: v}));
                              if (v.length >= 2) axios.get(`${SHOP_API}/work-orders`, { params: { q: v } }).then(r => setEditWOResults(r.data.slice(0, 5))).catch(() => {});
                              else setEditWOResults([]);
                            }}
                            className="w-full p-1.5 bg-slate-50 border border-slate-300 rounded text-xs font-mono" />
                          {editWOResults.length > 0 && (
                            <div className="absolute z-20 mt-1 w-full bg-white border border-slate-300 rounded shadow-lg max-h-32 overflow-y-auto">
                              {editWOResults.map(wo => (
                                <button key={wo.project_number} data-testid={`edit-wo-option-${wo.project_number}`}
                                  onMouseDown={(e) => { e.preventDefault(); setEditForm(f => ({...f, work_order_number: wo.project_number})); setEditWOResults([]); }}
                                  className="w-full text-left px-2 py-1.5 hover:bg-slate-100 text-xs">
                                  <span className="font-mono text-blue-600">{wo.project_number}</span> <span className="text-slate-500">{wo.client_name}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* PSI cascade — only when WO changed */}
                        {editForm.work_order_number !== editForm.orig_wo && (
                          <label className="flex items-center gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-1.5">
                            <input type="checkbox" data-testid="edit-update-psi-checkbox" checked={editForm.update_psi !== false}
                              onChange={(e) => setEditForm(f => ({...f, update_psi: e.target.checked}))} />
                            Also fix the linked Daily PSI ({editForm.orig_wo || '—'} → {editForm.work_order_number || '—'})
                          </label>
                        )}
                        {/* Convert contractor → employee */}
                        {editForm.is_contractor && (
                          <div className="bg-blue-50 border border-blue-200 rounded p-2 space-y-1">
                            <label className="text-[10px] text-blue-700 uppercase font-semibold flex items-center gap-1">
                              <UserCheck className="w-3 h-3" /> This is actually an employee
                            </label>
                            <select data-testid="edit-convert-employee-select" value={editForm.convert_to_employee_id || ''}
                              onChange={(e) => setEditForm(f => ({...f, convert_to_employee_id: e.target.value}))}
                              className="w-full p-1.5 bg-white border border-slate-300 rounded text-xs text-slate-800">
                              <option value="">Keep as contractor</option>
                              {allEmployees.slice().sort((a,b) => (a.employee_name||a.full_name||'').localeCompare(b.employee_name||b.full_name||'')).map(e => (
                                <option key={e.employee_id||e.id} value={e.employee_id||e.id}>{e.employee_name||e.full_name} ({e.shop_number||''})</option>
                              ))}
                            </select>
                            <p className="text-[10px] text-blue-600">Converts this contractor entry into a tracked employee clock-in.</p>
                            {editForm.convert_to_employee_id && editForm.typed_name && (
                              <label className="flex items-start gap-1.5 text-[11px] text-blue-800 pt-0.5" title="Saves this spelling as a known-as name on the employee so the Daily PSI matcher links it automatically next time">
                                <input type="checkbox" checked={editForm.remember_alias !== false} onChange={(e) => setEditForm(f => ({...f, remember_alias: e.target.checked}))}
                                  data-testid="edit-remember-alias" className="mt-0.5 rounded" />
                                <span>Remember <b>"{editForm.typed_name}"</b> as this person — auto-match it on future PSIs</span>
                              </label>
                            )}
                            {mergePrompt && (
                              <div className="mt-1 bg-amber-50 border border-amber-300 rounded p-2 space-y-1.5" data-testid="merge-prompt">
                                <p className="text-[11px] font-semibold text-amber-900">{mergePrompt.employee} already has hours that day on this WO — which do you want to keep?</p>
                                {mergePrompt.existing.map(x => (
                                  <button key={x.id} onClick={() => saveEdit(cardId, { merge_strategy: 'keep_existing' })} data-testid="merge-keep-existing"
                                    className="w-full text-left px-2 py-1.5 bg-white border border-emerald-300 hover:bg-emerald-50 rounded text-[11px] text-slate-800">
                                    <b>Keep existing {x.hours != null ? `${x.hours}h` : ''}</b> — {toETLocalInput(x.clock_in_time).slice(11)}–{x.clock_out_time ? toETLocalInput(x.clock_out_time).slice(11) : 'open'}{x.clocked_in_by ? ` · by ${x.clocked_in_by}` : ''}{x.source === 'gps' || x.source === 'self' ? ' · self clock-in' : ''}
                                    <span className="block text-[10px] text-slate-500">Drops the "{mergePrompt.typed_name}" contractor row</span>
                                  </button>
                                ))}
                                <button onClick={() => saveEdit(cardId, { merge_strategy: 'keep_contractor' })} data-testid="merge-keep-contractor"
                                  className="w-full text-left px-2 py-1.5 bg-white border border-blue-300 hover:bg-blue-50 rounded text-[11px] text-slate-800">
                                  <b>Use the crew clock-in {mergePrompt.contractor.hours != null ? `${mergePrompt.contractor.hours}h` : ''}</b> — {toETLocalInput(mergePrompt.contractor.clock_in_time).slice(11)}–{mergePrompt.contractor.clock_out_time ? toETLocalInput(mergePrompt.contractor.clock_out_time).slice(11) : 'open'}{mergePrompt.contractor.clocked_in_by ? ` · by ${mergePrompt.contractor.clocked_in_by}` : ''}
                                  <span className="block text-[10px] text-slate-500">Replaces {mergePrompt.employee}'s existing hours for the day (re-approve after)</span>
                                </button>
                                <button onClick={() => setMergePrompt(null)} className="text-[10px] text-slate-500 underline">Cancel</button>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <button onClick={() => saveEdit(cardId)} data-testid="edit-save-btn"
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium">Save</button>
                          <button onClick={() => setEditing(null)}
                            className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded text-xs">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      /* Read Mode */
                      <>
                        <div className="flex gap-4 text-xs text-slate-600 flex-wrap">
                          <span>In: {fmtTime(c.clock_in_time)}</span>
                          <span>Out: {c.clock_out_time ? fmtTime(c.clock_out_time) : 'Still on site'}</span>
                          <span>Shift: {c.shift}</span>
                          {c.is_late && <span className="text-red-600">{c.late_minutes}m late</span>}
                        </div>
                        {c.wo_summary?.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs text-slate-500 font-semibold uppercase">Work Orders</p>
                            {c.wo_summary.map((wo, i) => (
                              <div key={i} className="flex justify-between text-xs bg-slate-50 rounded px-2 py-1">
                                <span className="font-mono text-blue-600">{wo.wo}</span>
                                <span className="text-slate-500">{wo.customer}</span>
                                <span className="font-bold text-slate-900">{wo.hours}h</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {c.approved_by && <p className="text-xs text-emerald-600">Approved by: {c.approved_by}</p>}
                        {(c.location || c.clock_in_lat) && (
                          <div>
                            <p className="text-xs text-slate-500 font-semibold uppercase mb-1 flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> PSI Location
                            </p>
                            <iframe title="GPS" className="w-full h-32 rounded-lg border border-slate-200"
                              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${c.location?.lat || c.clock_in_lat},${c.location?.lng || c.clock_in_lng}&zoom=14`}
                              allowFullScreen />
                          </div>
                        )}
                        <div className="flex gap-2 pt-1 flex-wrap">
                          <button onClick={() => startEdit(c)}
                            className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded text-[10px] font-medium flex items-center gap-1 hover:bg-blue-200">
                            <Edit2 className="w-3 h-3" /> Edit
                          </button>
                          <button onClick={() => deleteEvent(cardId, c.employee_name)}
                            className="px-2.5 py-1 bg-red-100 text-red-700 rounded text-[10px] font-medium flex items-center gap-1 hover:bg-red-200">
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                          {!c.is_contractor && c.source !== 'psi_crew_clockin' && (
                            <button onClick={() => moveToShop(c)}
                              data-testid={`move-to-shop-${cardId}`}
                              title="Clocked into the wrong tracker — move this day to Shop Tracker"
                              className="px-2.5 py-1 bg-slate-200 text-slate-800 rounded text-[10px] font-medium flex items-center gap-1 hover:bg-slate-300">
                              → Move to Shop Tracker
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Clocked into Shop Tracker — pull anyone who picked the wrong tracker onto a field job */}
          {data.in_shop_today?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="in-shop-today">
              <h4 className="text-xs text-amber-800 font-semibold uppercase mb-2 flex items-center gap-1">
                <Users className="w-3 h-3" /> Clocked into Shop Tracker today ({data.in_shop_today.length}) — pull to a field job if they're actually on site
              </h4>
              <div className="space-y-2">
                {data.in_shop_today.map(s => (
                  <div key={s.employee_id} className="bg-white rounded-lg px-3 py-2" data-testid={`in-shop-${s.employee_id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-sm text-slate-800">
                        <span className="font-semibold text-slate-900">{s.employee_name}</span>
                        <span className="text-xs text-slate-500 ml-2">{fmtTime(s.clock_in_time)}{s.clock_out_time ? ` → ${fmtTime(s.clock_out_time)}` : ' · still clocked in'}</span>
                      </span>
                      <button onClick={() => { setPullFor(pullFor === s.employee_id ? null : s.employee_id); setPullWO(''); setPullSelected(null); setPullWOResults([]); }}
                        data-testid={`pull-to-field-${s.employee_id}`}
                        className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-semibold">
                        → Pull to Field
                      </button>
                    </div>
                    {pullFor === s.employee_id && (
                      <div className="mt-2 flex items-end gap-2 flex-wrap" data-testid={`pull-to-field-form-${s.employee_id}`}>
                        <div className="flex-1 min-w-[180px] relative">
                          <label className="text-[10px] text-slate-500">Field Work Order</label>
                          <input type="text" value={pullWO} onChange={e => searchPullWO(e.target.value)} placeholder="Search WO#…" autoFocus
                            data-testid={`pull-to-field-wo-${s.employee_id}`}
                            className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-slate-900 text-sm placeholder-slate-400 outline-none" />
                          {pullWOResults.length > 0 && (
                            <div className="absolute z-10 mt-1 w-full bg-white border border-slate-300 rounded shadow max-h-40 overflow-y-auto">
                              {pullWOResults.map(wo => (
                                <button key={wo.project_number} onClick={() => { setPullSelected(wo); setPullWO(wo.project_number); setPullWOResults([]); }}
                                  data-testid={`pull-to-field-wo-opt-${wo.project_number}`}
                                  className="w-full text-left px-2 py-1.5 hover:bg-slate-50 text-xs">
                                  <span className="font-mono text-blue-700 font-bold">{wo.project_number}</span> <span className="text-slate-700">{wo.client_name}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <button onClick={() => pullToField(s)} disabled={pulling || !(pullSelected || pullWO.trim())}
                          data-testid={`pull-to-field-confirm-${s.employee_id}`}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-xs font-bold">
                          {pulling ? 'Moving…' : 'Move day to Field'}
                        </button>
                        <button onClick={() => setPullFor(null)} className="px-2 py-1.5 text-slate-500 hover:text-slate-900 text-xs">Cancel</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Not Clocked In */}
          {data.not_clocked_in?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h4 className="text-xs text-slate-500 font-semibold uppercase mb-2 flex items-center gap-1">
                <UserX className="w-3 h-3" /> Not Clocked In ({data.not_clocked_in.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {data.not_clocked_in.map(u => (
                  <span key={u.id} className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">{u.full_name}</span>
                ))}
              </div>
            </div>
          )}

          {data.crew.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-500">No field crew data for this date</div>
          )}
            </>
          )}
        </div>
      )}

      {/* OT Prompt Modal removed — OT mode is now chosen via the 8+OT / All Reg / All OT buttons */}
    </div>
  );
}

/* ── Field Crew Tab (PM view) ───────────────────────────────────── */
function CrewTab() {
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/crew-today`);
      setCrew(r.data.events || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeCount = crew.filter(c => !c.clock_out_time).length;
  const doneCount = crew.filter(c => c.clock_out_time).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-slate-700">Field Crew — Today</h3>
          {activeCount > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{activeCount} active</span>}
          {doneCount > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-300">{doneCount} done</span>}
        </div>
        <button onClick={load} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {loading ? <div className="text-center py-8 text-slate-500">Loading...</div> :
        crew.length === 0 ? <div className="text-center py-12 text-slate-500">No field crew clocked in today</div> : (
        <div className="space-y-2">
          {crew.map(c => {
            const isActive = !c.clock_out_time;
            const isExpanded = expanded === c.id;
            const hasGPS = c.clock_in_lat && c.clock_in_lng;

            return (
              <div key={c.id} className={`rounded-xl border overflow-hidden transition-colors ${isActive ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}>
                {/* Header — click to expand */}
                <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-100"
                  onClick={() => setExpanded(isExpanded ? null : c.id)}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isActive ? 'bg-emerald-400 animate-pulse' : 'bg-slate-400'}`} />
                    <span className="text-slate-900 font-semibold">{c.employee_name}</span>
                    {c.is_contractor && <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Contractor</span>}
                    <span className="font-mono text-blue-700 text-sm">{c.work_order_number}</span>
                    {c.gps_verified && <MapPin className="w-3 h-3 text-emerald-700" />}
                    {c.gps_verified === false && hasGPS && <MapPin className="w-3 h-3 text-amber-700" />}
                    {!hasGPS && <span className="text-[10px] text-slate-500">{c.gps_status === 'denied' ? 'Denied' : (c.gps_status === 'unavailable' || c.gps_status === 'timeout') ? 'No fix' : 'No GPS'}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-500">
                      {fmtTime(c.clock_in_time)} {c.clock_out_time ? '— ' + fmtTime(c.clock_out_time) : ''}
                    </span>
                    <span className="text-sm text-slate-900 font-bold">{c.hours_worked ? c.hours_worked + 'h' : 'Active'}</span>
                    <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-slate-200 p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Info panel */}
                      <div className="space-y-2">
                        <div className="text-xs text-slate-500 space-y-1">
                          <p><span className="text-slate-500">Work Order:</span> <span className="text-slate-900 font-mono">{c.work_order_number}</span></p>
                          <p><span className="text-slate-500">Clock In:</span> <span className="text-slate-900">{fmtTime(c.clock_in_time)}</span></p>
                          {c.clock_out_time && <p><span className="text-slate-500">Clock Out:</span> <span className="text-slate-900">{fmtTime(c.clock_out_time)}</span></p>}
                          {c.hours_worked && <p><span className="text-slate-500">Hours:</span> <span className="text-slate-900 font-bold">{c.hours_worked}h</span></p>}
                          <p><span className="text-slate-500">GPS:</span> {c.gps_verified ? <span className="text-emerald-700">Verified on site</span> : hasGPS ? <span className="text-amber-700">{c.gps_distance_m ? c.gps_distance_m + 'm from site' : 'Location logged'}</span> : c.gps_status === 'denied' ? <span className="text-red-600">Location denied</span> : (c.gps_status === 'unavailable' || c.gps_status === 'timeout') ? <span className="text-amber-700">Couldn't get location</span> : c.gps_status === 'no_device' ? <span className="text-slate-500">No GPS on device</span> : <span className="text-slate-500">Not captured</span>}</p>
                          {hasGPS && <p><span className="text-slate-500">Coords:</span> <span className="text-slate-500">{Number(c.clock_in_lat).toFixed(5)}, {Number(c.clock_in_lng).toFixed(5)}</span></p>}
                        </div>

                        {/* Consumables */}
                        {c.consumables && c.consumables.length > 0 && (
                          <div>
                            <p className="text-xs text-slate-500 font-semibold mb-1">Consumables Today:</p>
                            <div className="flex flex-wrap gap-1">
                              {c.consumables.map(function(con, i) {
                                return <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-700 rounded border border-slate-300">{con.item_name} x{con.quantity}</span>;
                              })}
                            </div>
                          </div>
                        )}

                        {/* Future: Truck, trailer, equipment, crew */}
                        <div className="text-[10px] text-slate-600 italic pt-1">Truck, trailer, equipment, and crew details will populate from schedule</div>
                      </div>

                      {/* Map panel */}
                      {hasGPS && (
                        <div className="rounded-lg overflow-hidden border border-slate-300" style={{minHeight: '200px'}}>
                          <iframe
                            title="GPS Location"
                            width="100%"
                            height="200"
                            style={{border: 0}}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            src={'https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=' + c.clock_in_lat + ',' + c.clock_in_lng + '&zoom=15'}
                          />
                        </div>
                      )}
                      {!hasGPS && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center" style={{minHeight: '200px'}}>
                          <div className="text-center text-slate-500">
                            <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
                            <p className="text-xs">No GPS data</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ── Field Consumable Picker ────────────────────────────────────── */
function FieldConsumablePicker({ workOrder }) {
  const [consumables, setConsumables] = useState([]);
  const [picks, setPicks] = useState({});
  const [logged, setLogged] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/consumables-list`).then(r => setConsumables(r.data)).catch(() => {});
    if (workOrder) {
      axios.get(`${API}/my-consumables`, { params: { date: today(), work_order: workOrder } })
        .then(r => setLogged(r.data)).catch(() => {});
    }
  }, [workOrder]);

  const togglePick = (item) => {
    setPicks(prev => {
      const copy = { ...prev };
      if (copy[item.name] !== undefined) delete copy[item.name];
      else copy[item.name] = 1;
      return copy;
    });
  };

  const submitConsumables = async () => {
    const items = Object.entries(picks)
      .filter(([_, qty]) => (parseFloat(qty) || 0) > 0)
      .map(([name, qty]) => {
        const item = consumables.find(c => c.name === name) || {};
        return { name, quantity: parseFloat(qty), category: item.category || '', unit: item.unit || 'each' };
      });
    if (items.length === 0) { toast.error('Select consumables first'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/log-consumables`, { items, work_order_number: workOrder, date: today() });
      toast.success('Consumables logged');
      setPicks({});
      // Reload logged
      const r = await axios.get(`${API}/my-consumables`, { params: { date: today(), work_order: workOrder } });
      setLogged(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  const grouped = {};
  for (const c of consumables) {
    const cat = c.category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(c);
  }

  const selectedCount = Object.keys(picks).length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Package className="w-4 h-4 text-emerald-700" /> Consumables
          {logged.length > 0 && <span className="text-xs text-slate-500">({logged.length} logged today)</span>}
        </h3>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {/* Selected items */}
          {selectedCount > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(picks).map(([name, qty]) => (
                <div key={name} className="flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <span className="text-xs text-emerald-700">{name}</span>
                  <input type="number" inputMode="numeric" min="1" step="1" value={qty}
                    onChange={e => setPicks(prev => ({ ...prev, [name]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    className="w-12 px-1 py-0.5 bg-white/90 border border-emerald-300 rounded text-slate-900 text-center text-xs font-bold outline-none" />
                  <button onClick={() => togglePick({ name })} className="text-slate-500 hover:text-red-600">
                    <XCircle className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Quick pick grid by category */}
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <p className="text-xs text-slate-900 font-semibold mb-1 bg-slate-100 px-2 py-1 rounded">{cat}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                {items.map(item => {
                  const isActive = picks[item.name] !== undefined;
                  return (
                    <button key={item.name} onClick={() => togglePick(item)}
                      className={`px-2 py-1.5 text-xs rounded-lg border text-left transition-all ${
                        isActive ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-medium' : 'border-slate-300 bg-slate-50 text-slate-500 hover:border-slate-500'
                      }`}>
                      {item.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {selectedCount > 0 && (
            <button onClick={submitConsumables} disabled={submitting}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 text-white rounded-xl font-semibold text-sm">
              {submitting ? 'Logging...' : `Log ${selectedCount} Consumable(s)`}
            </button>
          )}

          {/* Already logged today */}
          {logged.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 font-semibold mb-1">Logged Today:</p>
              <div className="flex flex-wrap gap-1">
                {logged.map((c, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-700 rounded border border-slate-300">
                    {c.item_name} x{c.quantity}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ── Field Admin Tab ────────────────────────────────────────────── */
function FieldAdminTab() {
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [shop, setShop] = useState({ lat: '', lng: '', address: '' });
  const [shopSaving, setShopSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/travel-config`).then(r => {
      setShop({ lat: r.data.lat ?? '', lng: r.data.lng ?? '', address: r.data.address ?? '' });
    }).catch(() => {});
  }, []);

  const saveShop = async () => {
    if (shop.lat === '' || shop.lng === '') { toast.error('Enter latitude and longitude'); return; }
    setShopSaving(true);
    try {
      await axios.put(`${API}/travel-config`, { lat: parseFloat(shop.lat), lng: parseFloat(shop.lng), address: shop.address });
      toast.success('Shop location saved — travel distances will use this origin');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setShopSaving(false);
  };

  const purgeAll = async () => {
    if (!confirmPurge) { setConfirmPurge(true); return; }
    try {
      const r = await axios.post(`${API}/admin/purge-all`, { type: 'everything' });
      const items = Object.entries(r.data).filter(([k, v]) => k !== 'status' && v > 0).map(([k, v]) => `${v} ${k}`);
      toast.success(`Purged: ${items.join(', ') || 'nothing to purge'}`);
      setConfirmPurge(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
      setConfirmPurge(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      {/* Shop / office origin for travel distance */}
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="shop-location-card">
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="w-5 h-5 text-blue-700" />
          <p className="text-sm text-slate-900 font-medium">Shop / Office Location (travel origin)</p>
        </div>
        <p className="text-xs text-slate-500 mb-3">Travel distance for each job is measured from this point to the crew's GPS clock-in location.</p>
        <input value={shop.address} onChange={e => setShop({ ...shop, address: e.target.value })}
          placeholder="Address (label only)" data-testid="shop-address-input"
          className="w-full mb-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 text-sm outline-none" />
        <div className="flex gap-2">
          <input value={shop.lat} onChange={e => setShop({ ...shop, lat: e.target.value })}
            placeholder="Latitude" data-testid="shop-lat-input"
            className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 text-sm outline-none" />
          <input value={shop.lng} onChange={e => setShop({ ...shop, lng: e.target.value })}
            placeholder="Longitude" data-testid="shop-lng-input"
            className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 text-sm outline-none" />
          <button onClick={saveShop} disabled={shopSaving} data-testid="shop-save-btn"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
            {shopSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Trash2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-700 font-medium">Field Tracker — Admin Tools</p>
            <p className="text-xs text-red-600 mt-0.5">Purge all field clock events, timesheets, and consumable entries. Permanent.</p>
          </div>
        </div>
        {confirmPurge ? (
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={purgeAll}
              className="px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-lg text-xs font-bold animate-pulse">
              YES — PURGE ALL
            </button>
            <button onClick={() => setConfirmPurge(false)}
              className="px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-900 rounded-lg text-xs">
              Cancel
            </button>
          </div>
        ) : (
          <button onClick={purgeAll}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 whitespace-nowrap flex-shrink-0">
            <Trash2 className="w-4 h-4" /> Purge All Test Data
          </button>
        )}
      </div>
    </div>
  );
}

/* ── My Hours tab removed — employees now edit their own hours directly on the timesheet grid ── */
